// Registro de presets de colecciones por industria.
//
// El motor de colecciones (collection_types + collection_items) es agnóstico de
// industria; los presets lo "visten": cada preset es una definición lista para
// insertar como fila de `collection_types` (labels, ruta, icono, schemaJson).
//
// Lo consumen:
//   - el onboarding (POST /api/onboarding/types) al instanciar los tipos elegidos,
//   - el catálogo "¿Qué más quieres gestionar?" del panel (mismos presets),
//   - la generación LLM (el schema del preset es la especificación de salida).
//
// Regla de producto (docs/onboarding.md §1): el usuario nunca ve "colección".
// Aquí solo viven datos del motor; las etiquetas amigables salen de `labels`.

import { BLOG_COVER_MODES, BLOG_COVER_FOCAL_POINTS } from './blog-cover';

export type Locale = 'es' | 'en';

export type FieldType =
  | 'text' | 'richtext' | 'number' | 'price' | 'boolean'
  | 'select' | 'image' | 'gallery' | 'textlist' | 'videolist' | 'relation'
  | 'coordinates';

export type LocalizedText = Record<Locale, string>;

export interface FieldDef {
  key: string;
  type: FieldType;
  localized?: boolean;
  label: LocalizedText;
  help?: Partial<LocalizedText>;
  example?: Partial<LocalizedText>;
  section?: string;
  required?: boolean;
  default?: unknown;       // valor inicial para ítems nuevos (no afecta edición)
  format?: 'phone' | 'email' | 'url'; // solo text — tipo de input semántico
  options?: string[];      // solo select
  optionLabels?: Record<string, Partial<LocalizedText>>; // labels visibles; el valor no cambia
  relationTo?: string;     // solo relation → key de otro collection_type
  /** Resuelve candidatos por arquetipo en vez de por una key concreta. */
  relationArchetype?: Archetype;
  /** Permite que el índice público lo ofrezca como faceta. `false` lo excluye. */
  filterable?: boolean;
  /**
   * LST-11 — amplía la búsqueda textual del catálogo a este campo.
   *
   * El título de la tarjeta y la ubicación visible de un `listing` entran siempre; esta marca
   * existe para que un tenant sume un texto CORTO suyo (un modelo, una denominación) sin que
   * nadie acabe indexando descripciones enteras por accidente. `richtext`, media y URLs quedan
   * fuera aunque se marquen.
   */
  searchable?: boolean;
}

export type Archetype = 'catalog' | 'listing' | 'people' | 'social' | 'editorial' | 'ops';
export type JsonLdType =
  | 'RealEstateListing' | 'Person' | 'Article' | 'Service' | 'Product' | 'LocalBusiness' | null;

export interface CollectionTypePreset {
  key: string;                                   // = collection_types.key (único por tenant)
  archetype: Archetype;
  labels: { singular: LocalizedText; plural: LocalizedText };
  route: Record<Locale, string>;
  icon: string;                                  // nombre de icono lucide
  jsonLdType: JsonLdType;
  hasDetailPage: boolean;
  schema: FieldDef[];
  // Pista para el LLM: qué es y cómo suena el contenido de esta colección.
  aiHint: { es: string; en: string };
}

export interface IndustryDef {
  key: string;                                   // se guarda en tenants.industry
  label: LocalizedText;
  icon: string;
  blurb: LocalizedText;                          // una línea para la card de industria
  recommended: string[];                         // preset keys pre-seleccionados en el wizard
  compatible: string[];                          // presets que tienen sentido para esta industria
  // Labels que sobreescriben los del preset al instanciar (ej. "Equipo" → "Especialistas").
  labelOverrides?: Record<string, { singular: LocalizedText; plural: LocalizedText }>;
}

export interface SpecialtyDef {
  key: string;
  industryKey: string;
  label: LocalizedText;
}

// ─────────────────────────── Presets ───────────────────────────

const properties: CollectionTypePreset = {
  key: 'properties',
  archetype: 'listing',
  labels: { singular: { es: 'Propiedad', en: 'Property' }, plural: { es: 'Propiedades', en: 'Properties' } },
  route: { es: 'propiedades', en: 'properties' },
  icon: 'home',
  jsonLdType: 'RealEstateListing',
  hasDetailPage: true,
  aiHint: {
    es: 'Inmueble en venta o renta con descripción atractiva y especificaciones reales.',
    en: 'Property for sale or rent with attractive copy and realistic specs.',
  },
  schema: [
    { key: 'price', type: 'price', localized: false, filterable: true, label: { es: 'Precio', en: 'Price' }, section: 'Básico' },
    { key: 'currency', type: 'select', localized: false, label: { es: 'Moneda', en: 'Currency' }, options: ['MXN', 'USD'], default: 'MXN', section: 'Básico' },
    { key: 'type', type: 'select', localized: false, filterable: true, label: { es: 'Tipo de operación', en: 'Operation type' }, options: ['sale', 'rent'], optionLabels: { sale: { es: 'Venta', en: 'Sale' }, rent: { es: 'Renta', en: 'Rent' } }, section: 'Básico' },
    { key: 'status', type: 'select', localized: false, label: { es: 'Estado comercial', en: 'Listing status' }, help: { es: 'Indica si la propiedad sigue disponible para los visitantes.', en: 'Indicates whether the property is still available to visitors.' }, options: ['available', 'sold', 'rented'], optionLabels: { available: { es: 'Disponible', en: 'Available' }, sold: { es: 'Vendida', en: 'Sold' }, rented: { es: 'Rentada', en: 'Rented' } }, section: 'Básico' },
    { key: 'description', type: 'richtext', localized: true, label: { es: 'Descripción', en: 'Description' }, section: 'Básico' },
    { key: 'agent', type: 'relation', relationTo: 'agents', relationArchetype: 'people', localized: false, label: { es: 'Agente responsable', en: 'Responsible agent' }, help: { es: 'Persona que aparecerá vinculada a esta propiedad en su perfil público.', en: 'Person whose public profile will be linked to this property.' }, section: 'Básico' },
    { key: 'location', type: 'text', localized: false, label: { es: 'Ubicación visible', en: 'Display location' }, help: { es: 'Texto público que aparecerá en la tarjeta y la ficha de la propiedad.', en: 'Public text shown on the property card and detail page.' }, example: { es: 'Fracc. El Lago, Tijuana', en: 'El Lago, Tijuana' }, section: 'Ubicación' },
    { key: 'city', type: 'text', localized: false, filterable: true, label: { es: 'Ciudad o municipio', en: 'City or municipality' }, help: { es: 'Se usa para organizar y filtrar propiedades.', en: 'Used to organize and filter properties.' }, example: { es: 'Tijuana', en: 'Tijuana' }, section: 'Ubicación' },
    { key: 'neighborhood', type: 'text', localized: false, filterable: true, label: { es: 'Colonia o fraccionamiento', en: 'Neighborhood or development' }, help: { es: 'Zona específica dentro de la ciudad o municipio.', en: 'Specific area within the city or municipality.' }, example: { es: 'Fracc. El Lago', en: 'El Lago' }, section: 'Ubicación' },
    { key: 'coordinates', type: 'coordinates', localized: false, label: { es: 'Coordenadas', en: 'Coordinates' }, help: { es: 'Ubica la propiedad en el mapa. Puedes ocultar la dirección exacta por privacidad del dueño.', en: 'Pin the property on the map. You can hide the exact address for owner privacy.' }, section: 'Ubicación' },
    { key: 'bedrooms', type: 'number', localized: false, filterable: true, label: { es: 'Recámaras', en: 'Bedrooms' }, section: 'Detalles' },
    { key: 'bathrooms', type: 'number', localized: false, label: { es: 'Baños', en: 'Bathrooms' }, section: 'Detalles' },
    { key: 'parkingSpaces', type: 'number', localized: false, label: { es: 'Estacionamientos', en: 'Parking' }, section: 'Detalles' },
    { key: 'area', type: 'number', localized: false, label: { es: 'Superficie principal', en: 'Primary area' }, help: { es: 'Medida principal que se mostrará en tarjetas y resultados.', en: 'Primary measurement shown on cards and results.' }, example: { es: '195', en: '195' }, section: 'Detalles' },
    { key: 'areaUnit', type: 'select', localized: false, label: { es: 'Unidad de superficie', en: 'Area unit' }, help: { es: 'Unidad aplicada a superficie, construcción y terreno.', en: 'Unit applied to area, construction and lot measurements.' }, options: ['m²', 'ft²'], default: 'm²', section: 'Detalles' },
    { key: 'propertySubtype', type: 'select', localized: false, label: { es: 'Subtipo', en: 'Subtype' }, options: ['casa', 'departamento', 'terreno', 'local', 'oficina', 'bodega', 'rancho'], optionLabels: { casa: { es: 'Casa', en: 'House' }, departamento: { es: 'Departamento', en: 'Apartment' }, terreno: { es: 'Terreno', en: 'Land' }, local: { es: 'Local comercial', en: 'Commercial space' }, oficina: { es: 'Oficina', en: 'Office' }, bodega: { es: 'Bodega', en: 'Warehouse' }, rancho: { es: 'Rancho', en: 'Ranch' } }, section: 'Detalles' },
    { key: 'propertyCondition', type: 'select', localized: false, label: { es: 'Condición', en: 'Condition' }, options: ['nueva', 'usada', 'remodelada', 'en_construccion', 'en_planos'], optionLabels: { nueva: { es: 'Nueva', en: 'New' }, usada: { es: 'Usada', en: 'Pre-owned' }, remodelada: { es: 'Remodelada', en: 'Renovated' }, en_construccion: { es: 'En construcción', en: 'Under construction' }, en_planos: { es: 'En planos', en: 'Pre-construction' } }, section: 'Detalles' },
    { key: 'landUse', type: 'select', localized: false, label: { es: 'Uso de suelo', en: 'Land use' }, options: ['habitacional', 'comercial', 'mixto', 'industrial', 'turistico'], optionLabels: { habitacional: { es: 'Habitacional', en: 'Residential' }, comercial: { es: 'Comercial', en: 'Commercial' }, mixto: { es: 'Mixto', en: 'Mixed use' }, industrial: { es: 'Industrial', en: 'Industrial' }, turistico: { es: 'Turístico', en: 'Tourism' } }, section: 'Detalles' },
    { key: 'regime', type: 'select', localized: false, label: { es: 'Régimen', en: 'Regime' }, options: ['condominio', 'privada', 'copropiedad'], optionLabels: { condominio: { es: 'Condominio', en: 'Condominium' }, privada: { es: 'Propiedad privada', en: 'Private property' }, copropiedad: { es: 'Copropiedad', en: 'Co-ownership' } }, section: 'Detalles' },
    { key: 'furnished', type: 'select', localized: false, label: { es: 'Amueblado', en: 'Furnished' }, options: ['sin_amueblar', 'semi_amueblado', 'amueblado'], optionLabels: { sin_amueblar: { es: 'Sin amueblar', en: 'Unfurnished' }, semi_amueblado: { es: 'Semi-amueblado', en: 'Semi-furnished' }, amueblado: { es: 'Amueblado', en: 'Furnished' } }, section: 'Detalles' },
    { key: 'features', type: 'textlist', localized: true, label: { es: 'Características', en: 'Features' }, section: 'Detalles' },
    { key: 'nearbySchools', type: 'textlist', localized: true, label: { es: 'Escuelas cercanas', en: 'Nearby schools' }, help: { es: 'Agrega una escuela por renglón. Es información que tú verificaste; no se obtiene de un directorio automático.', en: 'Add one school per line. This is information you verified; it is not pulled from an automatic directory.' }, section: 'Ubicación' },
    { key: 'gallery', type: 'gallery', localized: false, label: { es: 'Galería', en: 'Gallery' }, section: 'Imágenes' },
  ],
};

const agents: CollectionTypePreset = {
  key: 'agents',
  archetype: 'people',
  labels: { singular: { es: 'Agente', en: 'Agent' }, plural: { es: 'Agentes', en: 'Agents' } },
  route: { es: 'agentes', en: 'agents' },
  icon: 'users',
  jsonLdType: 'Person',
  hasDetailPage: true,
  aiHint: {
    es: 'Asesor inmobiliario con puesto y una bio breve y profesional.',
    en: 'Real-estate agent with a role and a short professional bio.',
  },
  schema: [
    { key: 'role', type: 'text', localized: false, label: { es: 'Puesto', en: 'Role' }, section: 'Básico' },
    { key: 'photo', type: 'image', localized: false, label: { es: 'Foto', en: 'Photo' }, section: 'Básico' },
    { key: 'bio', type: 'richtext', localized: true, label: { es: 'Biografía', en: 'Bio' }, section: 'Básico' },
    { key: 'profileUrl', type: 'text', format: 'url', localized: false, label: { es: 'Perfil profesional', en: 'Professional profile' }, help: { es: 'Enlace a tu perfil profesional público, por ejemplo LinkedIn.', en: 'Link to your public professional profile, such as LinkedIn.' }, section: 'Contacto' },
    { key: 'email', type: 'text', format: 'email', localized: false, label: { es: 'Email', en: 'Email' }, section: 'Contacto' },
    { key: 'phone', type: 'text', format: 'phone', localized: false, label: { es: 'Teléfono', en: 'Phone' }, section: 'Contacto' },
    { key: 'whatsapp', type: 'text', format: 'phone', localized: false, label: { es: 'WhatsApp', en: 'WhatsApp' }, section: 'Contacto' },
    { key: 'office', type: 'relation', relationTo: 'offices', localized: false, label: { es: 'Sucursal', en: 'Branch' }, section: 'Organización' },
  ],
};

const menu: CollectionTypePreset = {
  key: 'menu',
  archetype: 'catalog',
  labels: { singular: { es: 'Platillo', en: 'Dish' }, plural: { es: 'Menú', en: 'Menu' } },
  route: { es: 'menu', en: 'menu' },
  icon: 'utensils',
  jsonLdType: 'Product',
  hasDetailPage: false,
  aiHint: {
    es: 'Platillo del menú con descripción apetecible y precio.',
    en: 'Menu dish with appetizing copy and a price.',
  },
  schema: [
    { key: 'description', type: 'richtext', localized: true, label: { es: 'Descripción', en: 'Description' }, section: 'Básico' },
    { key: 'price', type: 'price', localized: false, filterable: true, label: { es: 'Precio', en: 'Price' }, section: 'Básico' },
    { key: 'category', type: 'text', localized: false, filterable: true, label: { es: 'Categoría', en: 'Category' }, section: 'Básico' },
    { key: 'image', type: 'image', localized: false, label: { es: 'Foto', en: 'Photo' }, section: 'Imágenes' },
    { key: 'dietary', type: 'textlist', localized: true, label: { es: 'Dietético', en: 'Dietary' }, section: 'Detalles' },
  ],
};

const products: CollectionTypePreset = {
  key: 'products',
  archetype: 'catalog',
  labels: { singular: { es: 'Producto', en: 'Product' }, plural: { es: 'Productos', en: 'Products' } },
  route: { es: 'productos', en: 'products' },
  icon: 'shopping-bag',
  jsonLdType: 'Product',
  hasDetailPage: true,
  aiHint: {
    es: 'Producto a la venta con descripción que vende y precio.',
    en: 'Product for sale with persuasive copy and a price.',
  },
  schema: [
    { key: 'description', type: 'richtext', localized: true, label: { es: 'Descripción', en: 'Description' }, section: 'Básico' },
    { key: 'price', type: 'price', localized: false, filterable: true, label: { es: 'Precio', en: 'Price' }, section: 'Básico' },
    { key: 'currency', type: 'select', localized: false, label: { es: 'Moneda', en: 'Currency' }, options: ['MXN', 'USD'], section: 'Básico' },
    { key: 'sku', type: 'text', localized: false, label: { es: 'SKU', en: 'SKU' }, section: 'Detalles' },
    { key: 'features', type: 'textlist', localized: true, label: { es: 'Características', en: 'Features' }, section: 'Detalles' },
    { key: 'gallery', type: 'gallery', localized: false, label: { es: 'Galería', en: 'Gallery' }, section: 'Imágenes' },
  ],
};

const services: CollectionTypePreset = {
  key: 'services',
  archetype: 'catalog',
  labels: { singular: { es: 'Servicio', en: 'Service' }, plural: { es: 'Servicios', en: 'Services' } },
  route: { es: 'servicios', en: 'services' },
  icon: 'briefcase',
  jsonLdType: 'Service',
  hasDetailPage: true,
  aiHint: {
    es: 'Servicio que ofrece el negocio, con beneficio claro para el cliente.',
    en: 'Service the business offers, with a clear customer benefit.',
  },
  schema: [
    { key: 'summary', type: 'text', localized: true, label: { es: 'Resumen', en: 'Summary' }, section: 'Básico' },
    { key: 'description', type: 'richtext', localized: true, label: { es: 'Descripción', en: 'Description' }, section: 'Básico' },
    { key: 'price', type: 'price', localized: false, filterable: true, label: { es: 'Precio (opcional)', en: 'Price (optional)' }, section: 'Básico' },
    { key: 'currency', type: 'select', localized: false, default: 'MXN', label: { es: 'Moneda', en: 'Currency' }, options: ['MXN', 'USD'], section: 'Básico' },
    { key: 'availability', type: 'select', localized: false, filterable: true, default: 'available', label: { es: 'Disponibilidad', en: 'Availability' }, options: ['available', 'by_request', 'unavailable'], optionLabels: {
      available: { es: 'Disponible', en: 'Available' },
      by_request: { es: 'Bajo solicitud', en: 'By request' },
      unavailable: { es: 'No disponible', en: 'Unavailable' },
    }, section: 'Básico' },
    { key: 'icon', type: 'text', localized: false, label: { es: 'Icono', en: 'Icon' }, section: 'Básico' },
    { key: 'features', type: 'textlist', localized: true, label: { es: 'Incluye', en: 'Includes' }, section: 'Detalles' },
    { key: 'image', type: 'image', localized: false, label: { es: 'Imagen', en: 'Image' }, section: 'Imágenes' },
    { key: 'ctaLabel', type: 'text', localized: false, label: { es: 'Texto del botón', en: 'Button label' }, section: 'Acción' },
    { key: 'ctaHref', type: 'text', localized: false, format: 'url', label: { es: 'Enlace del botón', en: 'Button link' }, section: 'Acción' },
  ],
};

const team: CollectionTypePreset = {
  key: 'team',
  archetype: 'people',
  labels: { singular: { es: 'Persona', en: 'Person' }, plural: { es: 'Equipo', en: 'Team' } },
  route: { es: 'equipo', en: 'team' },
  icon: 'users',
  jsonLdType: 'Person',
  hasDetailPage: true,
  aiHint: {
    es: 'Integrante del equipo con puesto y bio breve y cercana.',
    en: 'Team member with a role and a short, warm bio.',
  },
  schema: [
    { key: 'role', type: 'text', localized: false, label: { es: 'Puesto', en: 'Role' }, section: 'Básico' },
    { key: 'photo', type: 'image', localized: false, label: { es: 'Foto', en: 'Photo' }, section: 'Básico' },
    { key: 'bio', type: 'richtext', localized: true, label: { es: 'Biografía', en: 'Bio' }, section: 'Básico' },
    { key: 'profileUrl', type: 'text', format: 'url', localized: false, label: { es: 'Perfil profesional', en: 'Professional profile' }, help: { es: 'Enlace a tu perfil profesional público, por ejemplo LinkedIn.', en: 'Link to your public professional profile, such as LinkedIn.' }, section: 'Contacto' },
    { key: 'email', type: 'text', format: 'email', localized: false, label: { es: 'Email', en: 'Email' }, section: 'Contacto' },
    { key: 'phone', type: 'text', format: 'phone', localized: false, label: { es: 'Teléfono', en: 'Phone' }, section: 'Contacto' },
  ],
};

const testimonials: CollectionTypePreset = {
  key: 'testimonials',
  archetype: 'social',
  labels: { singular: { es: 'Testimonio', en: 'Testimonial' }, plural: { es: 'Testimonios', en: 'Testimonials' } },
  route: { es: 'testimonios', en: 'testimonials' },
  icon: 'message-square-quote',
  jsonLdType: null,
  hasDetailPage: false,
  aiHint: {
    es: 'Reseña de un cliente satisfecho, en primera persona y creíble.',
    en: 'Review from a happy customer, first person and believable.',
  },
  schema: [
    { key: 'role', type: 'text', localized: false, label: { es: 'Puesto / Empresa', en: 'Role / Company' }, section: 'Básico' },
    { key: 'quote', type: 'richtext', localized: true, label: { es: 'Testimonio', en: 'Quote' }, section: 'Básico' },
    { key: 'rating', type: 'number', localized: false, label: { es: 'Calificación (1-5)', en: 'Rating (1-5)' }, section: 'Básico' },
    { key: 'avatar', type: 'image', localized: false, label: { es: 'Foto', en: 'Photo' }, section: 'Básico' },
  ],
};

const faq: CollectionTypePreset = {
  key: 'faq',
  archetype: 'ops',
  labels: { singular: { es: 'Pregunta', en: 'Question' }, plural: { es: 'Preguntas frecuentes', en: 'FAQ' } },
  route: { es: 'preguntas-frecuentes', en: 'faq' },
  icon: 'help-circle',
  jsonLdType: null,
  hasDetailPage: false,
  aiHint: {
    es: 'Pregunta frecuente con una respuesta clara y útil.',
    en: 'Frequently asked question with a clear, helpful answer.',
  },
  schema: [
    { key: 'answer', type: 'richtext', localized: true, label: { es: 'Respuesta', en: 'Answer' }, section: 'Básico' },
    { key: 'category', type: 'text', localized: false, label: { es: 'Categoría', en: 'Category' }, section: 'Básico' },
  ],
};

const offices: CollectionTypePreset = {
  key: 'offices',
  archetype: 'ops',
  labels: { singular: { es: 'Sucursal', en: 'Office' }, plural: { es: 'Sucursales', en: 'Offices' } },
  route: { es: 'sucursales', en: 'offices' },
  icon: 'map-pin',
  jsonLdType: 'LocalBusiness',
  hasDetailPage: false,
  aiHint: {
    es: 'Sucursal u oficina física del negocio con dirección, horarios y datos de contacto propios.',
    en: 'Physical branch or office with its own address, hours, and contact details.',
  },
  schema: [
    { key: 'address', type: 'text', localized: false, label: { es: 'Dirección visible', en: 'Display address' }, help: { es: 'Dirección que aparecerá en el sitio.', en: 'Address shown on the site.' }, section: 'Ubicación' },
    { key: 'coordinates', type: 'coordinates', localized: false, label: { es: 'Mapa', en: 'Map' }, help: { es: 'Ubica la sucursal en el mapa.', en: 'Pin this office on the map.' }, section: 'Ubicación' },
    { key: 'hours', type: 'text', localized: true, label: { es: 'Horario', en: 'Hours' }, help: { es: 'Ej: Lun-Vie 9–18 h, Sáb 10–14 h', en: 'E.g. Mon–Fri 9 am–6 pm, Sat 10 am–2 pm' }, section: 'Horario' },
    { key: 'phone', type: 'text', format: 'phone', localized: false, label: { es: 'Teléfono', en: 'Phone' }, section: 'Contacto' },
    { key: 'whatsapp', type: 'text', format: 'phone', localized: false, label: { es: 'WhatsApp', en: 'WhatsApp' }, section: 'Contacto' },
    { key: 'email', type: 'text', format: 'email', localized: false, label: { es: 'Email', en: 'Email' }, section: 'Contacto' },
    { key: 'isMain', type: 'boolean', localized: false, label: { es: 'Sede principal', en: 'Main office' }, help: { es: 'Marca si esta es la oficina o sede principal del negocio.', en: 'Mark if this is the main business location.' }, section: 'Estado' },
  ],
};

// GEO-49 — sentinel guardado en `data.author` cuando el editor elige "El negocio" en vez de
// un integrante del equipo. No es un id real de `collection_items`: `articlePosting()`
// (`apps/site/src/lib/jsonld.ts`) lo detecta y publica el nodo del negocio como autor, no un
// `Person` — una firma de marca o de equipo nunca debe tipificarse como persona.
export const BUSINESS_AUTHOR_ID = '__business__';

export const BLOG_CALLOUT_ICONS = ['Lightbulb', 'Sparkles', 'Compass', 'MessageCircle', 'CheckCircle2'] as const;
export type BlogCalloutIcon = (typeof BLOG_CALLOUT_ICONS)[number];

const posts: CollectionTypePreset = {
  key: 'posts',
  archetype: 'editorial',
  labels: { singular: { es: 'Artículo', en: 'Post' }, plural: { es: 'Blog', en: 'Blog' } },
  route: { es: 'blog', en: 'blog' },
  icon: 'newspaper',
  jsonLdType: 'Article',
  hasDetailPage: true,
  aiHint: {
    es: 'Artículo de blog con extracto y cuerpo de varios párrafos, tono experto y cercano.',
    en: 'Blog post with an excerpt and a multi-paragraph body, expert yet warm tone.',
  },
  schema: [
    { key: 'excerpt', type: 'richtext', localized: true, label: { es: 'Extracto', en: 'Excerpt' }, section: 'Básico' },
    { key: 'body', type: 'richtext', localized: true, label: { es: 'Contenido', en: 'Content' }, section: 'Básico' },
    { key: 'cover', type: 'image', localized: false, label: { es: 'Portada', en: 'Cover' }, section: 'Imágenes' },
    { key: 'coverMode', type: 'select', localized: false, options: [...BLOG_COVER_MODES], optionLabels: {
      crop: { es: 'Fotografía · recortar', en: 'Photo · crop' },
      full: { es: 'Pieza editorial · mostrar completa', en: 'Editorial artwork · show in full' },
    }, label: { es: 'Presentación de portada', en: 'Cover presentation' }, section: 'Imágenes' },
    { key: 'coverFocus', type: 'select', localized: false, options: [...BLOG_COVER_FOCAL_POINTS], optionLabels: {
      center: { es: 'Centro', en: 'Center' }, top: { es: 'Arriba', en: 'Top' }, bottom: { es: 'Abajo', en: 'Bottom' },
      left: { es: 'Izquierda', en: 'Left' }, right: { es: 'Derecha', en: 'Right' },
      'top-left': { es: 'Arriba izquierda', en: 'Top left' }, 'top-right': { es: 'Arriba derecha', en: 'Top right' },
      'bottom-left': { es: 'Abajo izquierda', en: 'Bottom left' }, 'bottom-right': { es: 'Abajo derecha', en: 'Bottom right' },
    }, label: { es: 'Punto focal de la foto', en: 'Photo focal point' }, section: 'Imágenes' },
    { key: 'coverThumbnail', type: 'image', localized: false, label: { es: 'Miniatura opcional', en: 'Optional thumbnail' }, section: 'Imágenes' },
    { key: 'coverSocial', type: 'image', localized: false, label: { es: 'Imagen para compartir', en: 'Social sharing image' }, section: 'Imágenes' },
    { key: 'coverAlt', type: 'text', localized: true, label: { es: 'Descripción de la portada', en: 'Cover description' }, section: 'Imágenes' },
    { key: 'category', type: 'text', localized: false, label: { es: 'Categoría', en: 'Category' }, section: 'Básico' },
    { key: 'tags', type: 'textlist', localized: false, label: { es: 'Etiquetas', en: 'Tags' }, section: 'Básico' },
    { key: 'author', type: 'relation', relationTo: 'people', relationArchetype: 'people', localized: false, label: { es: 'Autor', en: 'Author' }, section: 'Básico' },
    { key: 'calloutTitle', type: 'text', localized: true, label: { es: 'Título del destacado', en: 'Callout title' }, section: 'Destacado editorial' },
    { key: 'calloutBody', type: 'richtext', localized: true, label: { es: 'Texto del destacado', en: 'Callout body' }, section: 'Destacado editorial' },
    { key: 'calloutIcon', type: 'select', localized: false, options: [...BLOG_CALLOUT_ICONS], label: { es: 'Icono del destacado', en: 'Callout icon' }, section: 'Destacado editorial' },
  ],
};

type FieldGuidance = Pick<FieldDef, 'help' | 'example'>;

// La orientación pertenece a la combinación colección/campo. No se infiere por
// tipo porque una misma key puede tener propósitos distintos entre industrias.
const FIELD_GUIDANCE: Record<string, Record<string, FieldGuidance>> = {
  properties: {
    price: { help: { es: 'Precio numérico de venta o renta, sin símbolos ni separadores.', en: 'Numeric sale or rental price, without symbols or separators.' } },
    currency: { help: { es: 'Moneda en la que se expresa el precio.', en: 'Currency used for the price.' } },
    description: { help: { es: 'Descripción comercial completa que verá el visitante.', en: 'Full marketing description shown to visitors.' } },
    bedrooms: { help: { es: 'Número total de recámaras de la propiedad.', en: 'Total number of bedrooms in the property.' } },
    bathrooms: { help: { es: 'Número de baños completos.', en: 'Number of full bathrooms.' } },
    parkingSpaces: { help: { es: 'Cantidad de vehículos que caben en el estacionamiento.', en: 'Number of vehicles that fit in the parking area.' } },
    propertySubtype: { help: { es: 'Clase específica de inmueble, por ejemplo casa, departamento o terreno.', en: 'Specific property class, such as house, apartment, or lot.' } },
    propertyCondition: { help: { es: 'Estado físico o etapa actual del inmueble.', en: 'Current physical condition or development stage of the property.' } },
    landUse: { help: { es: 'Uso de suelo legal o permitido para el predio.', en: 'Legal or permitted land use for the property.' } },
    regime: { help: { es: 'Régimen legal o administrativo, por ejemplo condominio o propiedad privada.', en: 'Legal or administrative regime, such as condominium or private property.' } },
    furnished: { help: { es: 'Indica si se entrega amueblada y en qué nivel.', en: 'Indicates whether and to what extent the property is furnished.' } },
    features: { help: { es: 'Amenidades o atributos destacados, uno por renglón.', en: 'Highlighted amenities or attributes, one per line.' } },
    gallery: { help: { es: 'Imágenes públicas de la propiedad; la primera funciona como portada.', en: 'Public property images; the first one is used as the cover.' } },
  },
  agents: {
    role: { help: { es: 'Cargo profesional que se mostrará junto al nombre.', en: 'Professional role displayed next to the name.' }, example: { es: 'Asesor inmobiliario', en: 'Real-estate advisor' } },
    photo: { help: { es: 'Retrato público del agente o asesor.', en: 'Public portrait of the agent or advisor.' } },
    bio: { help: { es: 'Presentación profesional breve para generar confianza.', en: 'Short professional introduction intended to build trust.' } },
    email: { help: { es: 'Correo público de contacto del agente.', en: 'Agent public contact email.' }, example: { es: 'nombre@empresa.com', en: 'name@company.com' } },
    phone: { help: { es: 'Teléfono de contacto; incluye lada cuando sea necesario.', en: 'Contact phone number; include area or country code when needed.' }, example: { es: '+52 664 123 4567', en: '+1 619 555 0100' } },
    whatsapp: { help: { es: 'Número que recibirá mensajes de WhatsApp, con código de país.', en: 'Number that receives WhatsApp messages, including country code.' }, example: { es: '+52 664 123 4567', en: '+1 619 555 0100' } },
    office: { help: { es: 'Sucursal a la que pertenece este agente. Requiere tener al menos una sucursal creada.', en: 'Branch this agent belongs to. Requires at least one office to be created first.' } },
  },
  menu: {
    description: { help: { es: 'Descripción breve y apetecible del platillo.', en: 'Short, appealing description of the dish.' } },
    price: { help: { es: 'Precio del platillo, sin símbolo de moneda.', en: 'Dish price, without a currency symbol.' } },
    category: { help: { es: 'Sección del menú donde se agrupará el platillo.', en: 'Menu section used to group this dish.' }, example: { es: 'Entradas', en: 'Starters' } },
    image: { help: { es: 'Fotografía principal que representa el platillo.', en: 'Main photograph representing the dish.' } },
    dietary: { help: { es: 'Indicaciones como vegano, vegetariano o sin gluten.', en: 'Labels such as vegan, vegetarian, or gluten-free.' } },
  },
  products: {
    description: { help: { es: 'Descripción comercial completa del producto.', en: 'Full marketing description of the product.' } },
    price: { help: { es: 'Precio numérico del producto, sin símbolos.', en: 'Numeric product price, without symbols.' } },
    currency: { help: { es: 'Moneda en la que se expresa el precio.', en: 'Currency used for the price.' } },
    sku: { help: { es: 'Código interno único para identificar el producto.', en: 'Unique internal code used to identify the product.' }, example: { es: 'CAM-NEG-M', en: 'SHIRT-BLK-M' } },
    features: { help: { es: 'Características o beneficios principales, uno por renglón.', en: 'Main features or benefits, one per line.' } },
    gallery: { help: { es: 'Imágenes del producto; la primera funciona como portada.', en: 'Product images; the first one is used as the cover.' } },
  },
  services: {
    summary: { help: { es: 'Frase corta para tarjetas y listados del servicio.', en: 'Short sentence used on service cards and listings.' } },
    description: { help: { es: 'Explicación completa del servicio y su beneficio.', en: 'Full explanation of the service and its benefit.' } },
    price: { help: { es: 'Precio de referencia; déjalo vacío si requiere cotización.', en: 'Reference price; leave empty when a quote is required.' } },
    icon: { help: { es: 'Nombre del icono Lucide usado para representar el servicio.', en: 'Lucide icon name used to represent the service.' }, example: { es: 'briefcase', en: 'briefcase' } },
    features: { help: { es: 'Entregables o beneficios incluidos, uno por renglón.', en: 'Included deliverables or benefits, one per line.' } },
    image: { help: { es: 'Imagen principal que representa el servicio.', en: 'Main image representing the service.' } },
  },
  team: {
    role: { help: { es: 'Cargo que se mostrará junto al nombre de la persona.', en: 'Role displayed next to the person name.' } },
    photo: { help: { es: 'Retrato público del integrante del equipo.', en: 'Public portrait of the team member.' } },
    bio: { help: { es: 'Presentación breve de su experiencia y responsabilidades.', en: 'Short introduction to their experience and responsibilities.' } },
    email: { help: { es: 'Correo público de contacto.', en: 'Public contact email.' } },
    phone: { help: { es: 'Teléfono público de contacto.', en: 'Public contact phone number.' } },
  },
  testimonials: {
    role: { help: { es: 'Puesto, empresa o contexto que identifica a quien opina.', en: 'Role, company, or context identifying the reviewer.' } },
    quote: { help: { es: 'Texto del testimonio tal como se mostrará públicamente.', en: 'Testimonial text as it will be displayed publicly.' } },
    rating: { help: { es: 'Calificación numérica de 1 a 5.', en: 'Numeric rating from 1 to 5.' } },
    avatar: { help: { es: 'Foto opcional de la persona que dio el testimonio.', en: 'Optional photo of the person who provided the testimonial.' } },
  },
  faq: {
    answer: { help: { es: 'Respuesta clara y directa a la pregunta usada como título.', en: 'Clear, direct answer to the question used as the title.' } },
    category: { help: { es: 'Tema utilizado para agrupar preguntas relacionadas.', en: 'Topic used to group related questions.' }, example: { es: 'Pagos', en: 'Payments' } },
  },
  posts: {
    excerpt: { help: { es: 'Resumen corto mostrado en tarjetas, listados y resultados.', en: 'Short summary shown on cards, listings, and results.' } },
    body: { help: { es: 'Contenido completo del artículo que verá el lector.', en: 'Full article content shown to the reader.' } },
    cover: { help: { es: 'Imagen principal del artículo para tarjetas y encabezado.', en: 'Main article image used on cards and the header.' } },
    coverMode: { help: { es: 'Elige pieza editorial si contiene texto, logo o un diagrama que debe verse entero.', en: 'Choose editorial artwork when text, a logo or a diagram must stay visible.' } },
    coverFocus: { help: { es: 'Ajusta qué parte permanece visible al recortar una fotografía.', en: 'Choose the part that stays visible when a photo is cropped.' } },
    coverThumbnail: { help: { es: 'Imagen opcional para tarjetas pequeñas. Si falta, se reutiliza la portada.', en: 'Optional image for small cards. The cover is used when absent.' } },
    coverSocial: { help: { es: 'Imagen opcional para enlaces compartidos. Si falta, se usa la portada.', en: 'Optional image for shared links. The cover is used when absent.' } },
    coverAlt: { help: { es: 'Describe lo que aporta la imagen además del título. Útil cuando lleva texto dentro.', en: 'Describe what the image adds beyond the title, especially embedded text.' } },
    category: { help: { es: 'Tema principal usado para organizar el blog.', en: 'Primary topic used to organize the blog.' }, example: { es: 'Consejos inmobiliarios', en: 'Real-estate advice' } },
    tags: { help: { es: 'Palabras clave más específicas, una por renglón.', en: 'More specific keywords, one per line.' } },
    calloutTitle: { help: { es: 'Título opcional de una idea clave que quieres destacar dentro del artículo.', en: 'Optional title for a key idea you want to highlight inside the article.' } },
    calloutBody: { help: { es: 'Explicación breve que acompaña al destacado; debe estar completa en cada idioma que publiques.', en: 'Short explanation for the callout; complete it in every language you publish.' } },
    calloutIcon: { help: { es: 'Icono decorativo del destacado editorial.', en: 'Decorative icon for the editorial callout.' } },
  },
  offices: {
    address: { help: { es: 'Dirección completa que verán los visitantes.', en: 'Full address shown to visitors.' }, example: { es: 'Av. Reforma 250, Col. Centro, Tijuana, B.C.', en: '250 Reforma Ave, Downtown, Tijuana, B.C.' } },
    hours: { help: { es: 'Horario de atención de esta sucursal.', en: 'Business hours for this location.' }, example: { es: 'Lun–Vie 9–18 h', en: 'Mon–Fri 9 am–6 pm' } },
    phone: { help: { es: 'Teléfono directo de la sucursal; incluye lada.', en: 'Branch direct phone; include area code.' }, example: { es: '+52 664 123 4567', en: '+1 619 555 0100' } },
    whatsapp: { help: { es: 'Número WhatsApp de la sucursal con código de país.', en: 'Branch WhatsApp number with country code.' } },
    email: { help: { es: 'Correo de contacto de esta sucursal.', en: 'Contact email for this branch.' } },
    isMain: { help: { es: 'Solo debe marcarse una sucursal como sede principal.', en: 'Only one location should be marked as main office.' } },
  },
};

function withGuidance(preset: CollectionTypePreset): CollectionTypePreset {
  const guidance = FIELD_GUIDANCE[preset.key] ?? {};
  return {
    ...preset,
    schema: preset.schema.map((field) => ({ ...guidance[field.key], ...field })),
  };
}

// Mapa por key — fuente única para instanciar y para el catálogo "añadir contenido".
export const PRESETS: Record<string, CollectionTypePreset> = {
  properties: withGuidance(properties),
  agents: withGuidance(agents),
  menu: withGuidance(menu),
  products: withGuidance(products),
  services: withGuidance(services),
  team: withGuidance(team),
  testimonials: withGuidance(testimonials),
  faq: withGuidance(faq),
  posts: withGuidance(posts),
  offices: withGuidance(offices),
};

// Presets que sirven a cualquier industria (catálogo "¿Qué más quieres gestionar?").
export const UNIVERSAL_PRESET_KEYS = ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'] as const;

// Giros que entregan un producto físico (vs. un servicio). Determina si políticas
// comerciales como "Envíos y entregas" tienen sentido para el negocio: una
// inmobiliaria, un despacho legal o un spa no envían nada. Usado tanto por las
// plantillas legales (apps/panel) como por el footer público (apps/site) para
// decidir si esa política aplica al tenant.
export const PRODUCT_INDUSTRIES = new Set(['comercio', 'restaurante', 'automotriz', 'construccion']);

export function industrySellsProducts(industry: string | null | undefined): boolean {
  return !!industry && PRODUCT_INDUSTRIES.has(industry);
}

// Industrias ofrecidas en el wizard. `recommended` define qué tipos se preseleccionan.
export const INDUSTRIES: IndustryDef[] = [
  {
    key: 'inmobiliaria',
    label: { es: 'Inmobiliaria', en: 'Real estate' },
    icon: 'home',
    blurb: { es: 'Venta y renta de propiedades: residencial, lujo, desarrollos, terrenos o renta vacacional.', en: 'Property sales and rentals: residential, luxury, developments, land or vacation rentals.' },
    recommended: ['properties', 'agents', 'posts', 'services', 'faq'],
    compatible:  ['properties', 'agents', 'posts', 'testimonials', 'faq', 'offices', 'services', 'team'],
    labelOverrides: {
      team: {
        singular: { es: 'Colaborador',   en: 'Team member' },
        plural:   { es: 'Colaboradores', en: 'Team members' },
      },
    },
  },
  {
    key: 'restaurante',
    label: { es: 'Restaurante', en: 'Restaurant' },
    icon: 'utensils',
    blurb: { es: 'Comida y bebida: restaurante, cafetería, café de especialidad, repostería o bar.', en: 'Food and drink: restaurant, coffee shop, specialty coffee, bakery or bar.' },
    recommended: ['menu', 'testimonials', 'faq', 'posts'],
    compatible:  ['menu', 'testimonials', 'faq', 'offices', 'team', 'posts'],
    labelOverrides: {
      team: {
        singular: { es: 'Colaborador', en: 'Staff member' },
        plural:   { es: 'Personal',    en: 'Staff' },
      },
    },
  },
  {
    key: 'salud',
    label: { es: 'Salud / Clínica', en: 'Health / Clinic' },
    icon: 'stethoscope',
    blurb: { es: 'Consultorios y clínicas: dentista, ortodoncia, nutrición, psicología o fisioterapia.', en: 'Practices and clinics: dentist, orthodontics, nutrition, psychology or physical therapy.' },
    recommended: ['services', 'team', 'testimonials', 'faq', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'offices', 'posts'],
    labelOverrides: {
      team: {
        singular: { es: 'Especialista', en: 'Specialist' },
        plural:   { es: 'Especialistas', en: 'Specialists' },
      },
    },
  },
  {
    key: 'servicios',
    label: { es: 'Servicios profesionales', en: 'Professional services' },
    icon: 'briefcase',
    blurb: { es: 'Servicios profesionales: consultoría, contabilidad, marketing, arquitectura o servicios de TI.', en: 'Professional services: consulting, accounting, marketing, architecture or IT services.' },
    recommended: ['services', 'team', 'testimonials', 'faq', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'posts', 'faq', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Profesional',   en: 'Professional' },
        plural:   { es: 'Profesionales', en: 'Professionals' },
      },
    },
  },
  {
    key: 'comercio',
    label: { es: 'Comercio / Tienda', en: 'Commerce / Shop' },
    icon: 'shopping-bag',
    blurb: { es: 'Tiendas y comercios: boutique, mueblería, floristería, farmacia o venta en línea.', en: 'Shops and retail: boutique, furniture store, flower shop, pharmacy or online store.' },
    recommended: ['products', 'testimonials', 'faq', 'posts'],
    compatible:  ['products', 'services', 'testimonials', 'faq', 'offices', 'team', 'posts'],
    labelOverrides: {
      team: {
        singular: { es: 'Colaborador',   en: 'Team member' },
        plural:   { es: 'Colaboradores', en: 'Team members' },
      },
    },
  },
  {
    key: 'belleza',
    label: { es: 'Belleza / Estética', en: 'Beauty / Salon' },
    icon: 'scissors',
    blurb: { es: 'Estética y cuidado personal: salón, barbería, spa, uñas o depilación.', en: 'Beauty and personal care: salon, barbershop, spa, nails or hair removal.' },
    recommended: ['services', 'team', 'testimonials', 'faq', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'products'],
    labelOverrides: {
      team: {
        singular: { es: 'Estilista',   en: 'Stylist' },
        plural:   { es: 'Estilistas',  en: 'Stylists' },
      },
    },
  },
  {
    key: 'educacion',
    label: { es: 'Educación', en: 'Education' },
    icon: 'graduation-cap',
    blurb: { es: 'Enseñanza y formación: academia de idiomas, cursos en línea, tutorías o música y arte.', en: 'Teaching and training: language academy, online courses, tutoring or music and arts.' },
    recommended: ['services', 'team', 'testimonials', 'faq', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Instructor',   en: 'Instructor' },
        plural:   { es: 'Instructores', en: 'Instructors' },
      },
      services: {
        singular: { es: 'Curso',   en: 'Course' },
        plural:   { es: 'Cursos',  en: 'Courses' },
      },
    },
  },
  {
    key: 'fitness',
    label: { es: 'Fitness / Bienestar', en: 'Fitness / Wellness' },
    icon: 'dumbbell',
    blurb: { es: 'Entrenamiento y bienestar: gimnasio, yoga, pilates o entrenamiento personal.', en: 'Training and wellness: gym, yoga, pilates or personal training.' },
    recommended: ['services', 'team', 'testimonials', 'faq'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Entrenador',   en: 'Trainer' },
        plural:   { es: 'Entrenadores', en: 'Trainers' },
      },
      services: {
        singular: { es: 'Clase',   en: 'Class' },
        plural:   { es: 'Clases',  en: 'Classes' },
      },
    },
  },
  {
    key: 'veterinaria',
    label: { es: 'Veterinaria / Mascotas', en: 'Veterinary / Pets' },
    icon: 'paw-print',
    blurb: { es: 'Cuidado de mascotas: clínica veterinaria, estética y baño, pensión o tienda.', en: 'Pet care: veterinary clinic, grooming, boarding or pet shop.' },
    recommended: ['services', 'team', 'testimonials', 'faq'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'products', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Veterinario',   en: 'Veterinarian' },
        plural:   { es: 'Veterinarios',  en: 'Veterinarians' },
      },
    },
  },
  {
    key: 'automotriz',
    label: { es: 'Automotriz', en: 'Automotive' },
    icon: 'wrench',
    blurb: { es: 'Servicios para autos: taller mecánico, hojalatería y pintura, detailing o refaccionaria.', en: 'Car services: auto repair shop, bodywork and paint, detailing or parts store.' },
    recommended: ['services', 'team', 'testimonials', 'faq'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Técnico',   en: 'Technician' },
        plural:   { es: 'Técnicos',  en: 'Technicians' },
      },
    },
  },
  {
    key: 'construccion',
    label: { es: 'Construcción / Diseño', en: 'Construction / Design' },
    icon: 'hard-hat',
    blurb: { es: 'Construcción y diseño: constructora, arquitecto, remodelaciones o interiorismo.', en: 'Construction and design: construction firm, architect, remodeling or interior design.' },
    recommended: ['services', 'team', 'testimonials', 'faq', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Especialista',   en: 'Specialist' },
        plural:   { es: 'Especialistas',  en: 'Specialists' },
      },
    },
  },
  {
    key: 'oficios',
    label: { es: 'Oficios / Servicios para el hogar', en: 'Trades / Home services' },
    icon: 'hammer',
    blurb: { es: 'Servicios a domicilio: plomería, electricidad, cerrajería, jardinería o reparaciones.', en: 'Home services: plumbing, electrical, locksmith, gardening or repairs.' },
    recommended: ['services', 'testimonials', 'faq'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Técnico',   en: 'Technician' },
        plural:   { es: 'Técnicos',  en: 'Technicians' },
      },
    },
  },
  {
    key: 'turismo',
    label: { es: 'Turismo / Hospedaje', en: 'Tourism / Hospitality' },
    icon: 'compass',
    blurb: { es: 'Turismo y hospedaje: hotel, hostal, tours, experiencias o agencia de viajes.', en: 'Tourism and lodging: hotel, hostel, tours, experiences or travel agency.' },
    recommended: ['services', 'testimonials', 'posts', 'faq'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Guía',   en: 'Guide' },
        plural:   { es: 'Guías',  en: 'Guides' },
      },
      services: {
        singular: { es: 'Tour',   en: 'Tour' },
        plural:   { es: 'Tours',  en: 'Tours' },
      },
    },
  },
  {
    key: 'legal',
    label: { es: 'Legal / Contable', en: 'Legal / Accounting' },
    icon: 'scale',
    blurb: { es: 'Despachos legales y contables: abogados, notaría, contabilidad o asesoría fiscal.', en: 'Legal and accounting firms: lawyers, notary, accounting or tax advisory.' },
    recommended: ['services', 'team', 'testimonials', 'faq', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Abogado',   en: 'Lawyer' },
        plural:   { es: 'Abogados',  en: 'Lawyers' },
      },
      services: {
        singular: { es: 'Área de práctica',   en: 'Practice area' },
        plural:   { es: 'Áreas de práctica',  en: 'Practice areas' },
      },
    },
  },
  {
    key: 'eventos',
    label: { es: 'Eventos / Bodas', en: 'Events / Weddings' },
    icon: 'calendar',
    blurb: { es: 'Eventos y bodas: organización, banquetes, fotografía, flores o música y DJ.', en: 'Events and weddings: planning, catering, photography, flowers or music and DJ.' },
    recommended: ['services', 'testimonials', 'posts', 'faq'],
    compatible:  ['services', 'products', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      services: {
        singular: { es: 'Paquete',   en: 'Package' },
        plural:   { es: 'Paquetes',  en: 'Packages' },
      },
      team: {
        singular: { es: 'Colaborador',   en: 'Collaborator' },
        plural:   { es: 'Colaboradores', en: 'Collaborators' },
      },
    },
  },
  {
    key: 'software',
    label: { es: 'Software / SaaS', en: 'Software / SaaS' },
    icon: 'monitor',
    blurb: { es: 'Aplicaciones web, SaaS y herramientas digitales: B2B, móvil, automatización o IA.', en: 'Web apps, SaaS and digital tools: B2B, mobile, automation or AI.' },
    recommended: ['services', 'testimonials', 'faq', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts'],
    labelOverrides: {
      services: {
        singular: { es: 'Plan',  en: 'Plan' },
        plural:   { es: 'Planes', en: 'Plans' },
      },
      team: {
        singular: { es: 'Fundador',   en: 'Founder' },
        plural:   { es: 'Equipo',     en: 'Team' },
      },
    },
  },
  {
    key: 'otro',
    label: { es: 'Otro', en: 'Other' },
    icon: 'sparkles',
    blurb: { es: '¿Tu giro no está en la lista? Empieza con lo esencial y arma tu sitio a tu medida.', en: "Don't see your type? Start with the essentials and build your site to fit." },
    recommended: ['services', 'testimonials', 'faq'],
    compatible:  [], // vacío = sin filtro, se muestran todos los presets disponibles
  },
];

// Especialidades normalizadas por industria. El onboarding las usa como
// autocomplete guiado para poblar business_profiles.subvertical.
export const SPECIALTIES: SpecialtyDef[] = [
  { key: 'bienes_raices_residencial', industryKey: 'inmobiliaria', label: { es: 'Bienes raíces residencial', en: 'Residential real estate' } },
  { key: 'bienes_raices_lujo', industryKey: 'inmobiliaria', label: { es: 'Bienes raíces de lujo', en: 'Luxury real estate' } },
  { key: 'renta_vacacional', industryKey: 'inmobiliaria', label: { es: 'Renta vacacional', en: 'Vacation rentals' } },
  { key: 'desarrollos_inmobiliarios', industryKey: 'inmobiliaria', label: { es: 'Desarrollos inmobiliarios', en: 'Real estate developments' } },
  { key: 'terrenos_e_inversion', industryKey: 'inmobiliaria', label: { es: 'Terrenos e inversión', en: 'Land and investment' } },

  { key: 'cafeteria', industryKey: 'restaurante', label: { es: 'Cafetería', en: 'Coffee shop' } },
  { key: 'cafe_especialidad', industryKey: 'restaurante', label: { es: 'Café de especialidad', en: 'Specialty coffee' } },
  { key: 'restaurante_familiar', industryKey: 'restaurante', label: { es: 'Restaurante familiar', en: 'Family restaurant' } },
  { key: 'brunch', industryKey: 'restaurante', label: { es: 'Brunch', en: 'Brunch' } },
  { key: 'reposteria', industryKey: 'restaurante', label: { es: 'Repostería', en: 'Bakery and pastries' } },
  { key: 'bar_y_cocteleria', industryKey: 'restaurante', label: { es: 'Bar y coctelería', en: 'Bar and cocktails' } },

  { key: 'dentista', industryKey: 'salud', label: { es: 'Dentista', en: 'Dentist' } },
  { key: 'ortodoncia', industryKey: 'salud', label: { es: 'Ortodoncia', en: 'Orthodontics' } },
  { key: 'medicina_estetica', industryKey: 'salud', label: { es: 'Medicina estética', en: 'Aesthetic medicine' } },
  { key: 'psicologia', industryKey: 'salud', label: { es: 'Psicología', en: 'Psychology' } },
  { key: 'nutricion', industryKey: 'salud', label: { es: 'Nutrición', en: 'Nutrition' } },
  { key: 'fisioterapia', industryKey: 'salud', label: { es: 'Fisioterapia', en: 'Physical therapy' } },

  { key: 'abogado', industryKey: 'servicios', label: { es: 'Abogado', en: 'Law firm' } },
  { key: 'contador', industryKey: 'servicios', label: { es: 'Contador', en: 'Accounting firm' } },
  { key: 'consultoria_empresarial', industryKey: 'servicios', label: { es: 'Consultoría empresarial', en: 'Business consulting' } },
  { key: 'marketing_digital', industryKey: 'servicios', label: { es: 'Marketing digital', en: 'Digital marketing' } },
  { key: 'despacho_arquitectura', industryKey: 'servicios', label: { es: 'Despacho de arquitectura', en: 'Architecture studio' } },
  { key: 'servicios_it', industryKey: 'servicios', label: { es: 'Servicios de TI', en: 'IT services' } },

  { key: 'tienda_online', industryKey: 'comercio', label: { es: 'Tienda en línea', en: 'Online store' } },
  { key: 'boutique', industryKey: 'comercio', label: { es: 'Boutique', en: 'Boutique' } },
  { key: 'floristeria', industryKey: 'comercio', label: { es: 'Floristería', en: 'Flower shop' } },
  { key: 'regalos_personalizados', industryKey: 'comercio', label: { es: 'Regalos personalizados', en: 'Custom gifts' } },
  { key: 'muebleria', industryKey: 'comercio', label: { es: 'Mueblería', en: 'Furniture store' } },
  { key: 'farmacia_minorista', industryKey: 'comercio', label: { es: 'Farmacia', en: 'Pharmacy' } },

  { key: 'salon_belleza', industryKey: 'belleza', label: { es: 'Salón de belleza', en: 'Beauty salon' } },
  { key: 'barberia', industryKey: 'belleza', label: { es: 'Barbería', en: 'Barbershop' } },
  { key: 'spa', industryKey: 'belleza', label: { es: 'Spa', en: 'Spa' } },
  { key: 'unas_y_manicura', industryKey: 'belleza', label: { es: 'Uñas y manicura', en: 'Nails and manicure' } },
  { key: 'depilacion_laser', industryKey: 'belleza', label: { es: 'Depilación láser', en: 'Laser hair removal' } },
  { key: 'cejas_y_pestanas', industryKey: 'belleza', label: { es: 'Cejas y pestañas', en: 'Brows and lashes' } },

  { key: 'academia_idiomas', industryKey: 'educacion', label: { es: 'Academia de idiomas', en: 'Language academy' } },
  { key: 'capacitacion_empresarial', industryKey: 'educacion', label: { es: 'Capacitación empresarial', en: 'Corporate training' } },
  { key: 'curso_online', industryKey: 'educacion', label: { es: 'Cursos en línea', en: 'Online courses' } },
  { key: 'tutoria_escolar', industryKey: 'educacion', label: { es: 'Tutoría escolar', en: 'School tutoring' } },
  { key: 'musica_y_arte', industryKey: 'educacion', label: { es: 'Música y arte', en: 'Music and arts' } },

  { key: 'gimnasio', industryKey: 'fitness', label: { es: 'Gimnasio', en: 'Gym' } },
  { key: 'yoga', industryKey: 'fitness', label: { es: 'Yoga', en: 'Yoga' } },
  { key: 'pilates', industryKey: 'fitness', label: { es: 'Pilates', en: 'Pilates' } },
  { key: 'entrenamiento_personal', industryKey: 'fitness', label: { es: 'Entrenamiento personal', en: 'Personal training' } },
  { key: 'wellness_corporativo', industryKey: 'fitness', label: { es: 'Wellness corporativo', en: 'Corporate wellness' } },

  { key: 'saas_b2b', industryKey: 'software', label: { es: 'SaaS B2B', en: 'B2B SaaS' } },
  { key: 'desarrollo_web', industryKey: 'software', label: { es: 'Desarrollo web', en: 'Web development' } },
  { key: 'apps_moviles', industryKey: 'software', label: { es: 'Apps móviles', en: 'Mobile apps' } },
  { key: 'automatizacion', industryKey: 'software', label: { es: 'Automatización', en: 'Automation' } },
  { key: 'consultoria_tecnologica', industryKey: 'software', label: { es: 'Consultoría tecnológica', en: 'Technology consulting' } },
  { key: 'ia_y_agentes', industryKey: 'software', label: { es: 'IA y agentes', en: 'AI and agents' } },
  { key: 'analitica_y_datos', industryKey: 'software', label: { es: 'Analítica y datos', en: 'Analytics and data' } },
  { key: 'ciberseguridad', industryKey: 'software', label: { es: 'Ciberseguridad', en: 'Cybersecurity' } },

  { key: 'plomeria', industryKey: 'oficios', label: { es: 'Plomería', en: 'Plumbing' } },
  { key: 'electricista', industryKey: 'oficios', label: { es: 'Electricista', en: 'Electrician' } },
  { key: 'cerrajeria', industryKey: 'oficios', label: { es: 'Cerrajería', en: 'Locksmith' } },
  { key: 'aire_acondicionado', industryKey: 'oficios', label: { es: 'Aire acondicionado y refrigeración', en: 'Air conditioning and refrigeration' } },
  { key: 'pintura', industryKey: 'oficios', label: { es: 'Pintura', en: 'Painting' } },
  { key: 'carpinteria', industryKey: 'oficios', label: { es: 'Carpintería', en: 'Carpentry' } },
  { key: 'herreria', industryKey: 'oficios', label: { es: 'Herrería', en: 'Metalwork' } },
  { key: 'jardineria', industryKey: 'oficios', label: { es: 'Jardinería', en: 'Gardening' } },
  { key: 'fumigacion', industryKey: 'oficios', label: { es: 'Fumigación y control de plagas', en: 'Pest control' } },
  { key: 'impermeabilizacion', industryKey: 'oficios', label: { es: 'Impermeabilización', en: 'Waterproofing' } },
  { key: 'limpieza', industryKey: 'oficios', label: { es: 'Limpieza', en: 'Cleaning services' } },
  { key: 'mudanzas', industryKey: 'oficios', label: { es: 'Mudanzas y fletes', en: 'Moving services' } },
];

export function getIndustry(key: string | null | undefined): IndustryDef | undefined {
  return INDUSTRIES.find((i) => i.key === key);
}

export function getSpecialtiesForIndustry(industryKey: string | null | undefined): SpecialtyDef[] {
  if (!industryKey) return [];
  return SPECIALTIES.filter((s) => s.industryKey === industryKey);
}

// Mapa industria (tenants.industry) → @type de schema.org para el nodo de negocio
// site-wide (lo que los motores generativos leen como "qué es este negocio"). Todos
// son subtipos de LocalBusiness/Organization, así que las referencias por @id tipadas
// como Organization siguen siendo válidas. Default LocalBusiness (genérico seguro).
const INDUSTRY_SCHEMA_TYPE: Record<string, string> = {
  inmobiliaria: 'RealEstateAgent',
  restaurante:  'Restaurant',
  salud:        'MedicalClinic',
  servicios:    'ProfessionalService',
  comercio:     'Store',
  belleza:      'BeautySalon',
  educacion:    'EducationalOrganization',
  fitness:      'ExerciseGym',
  veterinaria:  'VeterinaryCare',
  automotriz:   'AutomotiveBusiness',
  construccion: 'GeneralContractor',
  oficios:      'HomeAndConstructionBusiness',
  turismo:      'TravelAgency',
  legal:        'LegalService',
  eventos:      'ProfessionalService',
  // 'software'/SaaS no es un negocio local: usa Organization, no LocalBusiness.
  software:     'Organization',
  otro:         'LocalBusiness',
};

/** @type de schema.org para el negocio del tenant según su industria. */
export function industrySchemaType(industry: string | null | undefined): string {
  return (industry && INDUSTRY_SCHEMA_TYPE[industry]) || 'LocalBusiness';
}

/** Giros con `@type` propio declarado (ONB-14 §4 — inventario de cobertura). */
export const SCHEMA_TYPE_INDUSTRY_KEYS = Object.keys(INDUSTRY_SCHEMA_TYPE);

// CLI-08 — nombre en pantalla del "trabajo en curso" que se liga a un contacto:
// cómo se llama en pantalla lo decide el giro, no el tenant ni el código genérico.
// Cubre los cinco ejemplos de la ficha (operación/caso/obra/reparación/proyecto);
// el resto de industrias usa el default genérico.
const INDUSTRY_JOB_LABEL: Record<string, { singular: string; plural: string }> = {
  inmobiliaria: { singular: 'Operación', plural: 'Operaciones' },
  legal:        { singular: 'Caso', plural: 'Casos' },
  construccion: { singular: 'Obra', plural: 'Obras' },
  automotriz:   { singular: 'Reparación', plural: 'Reparaciones' },
  servicios:    { singular: 'Proyecto', plural: 'Proyectos' },
  software:     { singular: 'Proyecto', plural: 'Proyectos' },
  eventos:      { singular: 'Proyecto', plural: 'Proyectos' },
  salud:        { singular: 'Tratamiento', plural: 'Tratamientos' },
};

/** Nombre en pantalla del trabajo en curso (CLI-08) según la industria del tenant. */
export function industryJobLabel(industry: string | null | undefined): { singular: string; plural: string } {
  return (industry && INDUSTRY_JOB_LABEL[industry]) || { singular: 'Trabajo', plural: 'Trabajos' };
}

/** Giros con nombre propio del trabajo (ONB-14 §4 — inventario de cobertura). */
export const JOB_LABEL_INDUSTRY_KEYS = Object.keys(INDUSTRY_JOB_LABEL);

// ONB-12/ONB-14 — piso garantizado si la siembra por IA falla, no está configurada,
// agota el cupo diario o devuelve cero ítems: el tenant nunca debe terminar el
// onboarding con su catálogo principal vacío. ONB-14 sustituyó el mapa "solo servicios"
// por este catálogo de OFERTA INICIAL: cada giro declara además EN QUÉ colección vive su
// punto de partida, porque el wizard preselecciona `menu` a restaurante y `products` a
// comercio — sembrarles un servicio genérico era presentarlos como lo que no son.
//
// Cubre las 17 claves de `INDUSTRIES` (contrato verificado en `presets.test.ts`);
// `DEFAULT_STARTER_OFFER` queda reservado para una clave futura o heredada, nunca para
// una opción vigente del wizard. `price` en pesos (unidades monetarias, no centavos) y
// se omite cuando la oferta típicamente se cotiza caso por caso.
//
// Frontera de producto: productos y menú son VITRINA. Nada aquí supone inventario,
// cobro, disponibilidad en tiempo real ni reserva — es contenido editable que el dueño
// ajusta o borra.
export type StarterOfferCollectionKey = 'services' | 'menu' | 'products';

export interface StarterOfferItem {
  title: string;
  /** Frase corta que describe la oferta. Viaja a `summary` en `services` y a
   *  `description` en `menu`/`products`: mismo texto, el campo que cada preset usa. */
  summary: string;
  price?: number;
  /** Solo `menu`: sección donde se agrupa el platillo. */
  category?: string;
}

export interface IndustryStarterOffer {
  collectionKey: StarterOfferCollectionKey;
  items: StarterOfferItem[];
}

// Punto de partida neutro de cada colección. Lo usan los giros cuya oferta ES esa
// colección y, como red, cualquier tenant que siembre una de las otras dos (un
// restaurante que además activó servicios no debe recibir ahí los platillos de su menú).
const GENERIC_SERVICE_ITEMS: StarterOfferItem[] = [
  { title: 'Servicio principal', summary: 'Describe aquí lo que más te piden tus clientes.', price: 500 },
  { title: 'Servicio complementario', summary: 'Una segunda opción para quienes ya te conocen.' },
];

const GENERIC_MENU_ITEMS: StarterOfferItem[] = [
  { title: 'Platillo de la casa', summary: 'El que más te piden: descríbelo como se lo cuentas a alguien que llega por primera vez.', price: 180, category: 'Platos fuertes' },
  { title: 'Entrada para compartir', summary: 'Algo ligero para empezar mientras llega el plato fuerte.', price: 120, category: 'Entradas' },
  { title: 'Bebida de la casa', summary: 'La bebida con la que más acompañan tus platillos.', price: 60, category: 'Bebidas' },
];

const GENERIC_PRODUCT_ITEMS: StarterOfferItem[] = [
  { title: 'Producto más vendido', summary: 'El que más te compran: cuenta para quién es y por qué gusta.', price: 450 },
  { title: 'Novedad de temporada', summary: 'Lo más reciente que llegó a tu tienda.', price: 350 },
];

const GENERIC_STARTER_ITEMS: Record<StarterOfferCollectionKey, StarterOfferItem[]> = {
  services: GENERIC_SERVICE_ITEMS,
  menu: GENERIC_MENU_ITEMS,
  products: GENERIC_PRODUCT_ITEMS,
};

const INDUSTRY_STARTER_OFFER: Record<string, IndustryStarterOffer> = {
  inmobiliaria: {
    collectionKey: 'services',
    items: [
      { title: 'Avalúo de propiedad', summary: 'Valuación profesional para conocer el precio justo de venta o renta.', price: 2500 },
      { title: 'Asesoría hipotecaria', summary: 'Acompañamiento para elegir y tramitar el crédito que más te conviene.' },
    ],
  },
  restaurante: { collectionKey: 'menu', items: GENERIC_MENU_ITEMS },
  salud: {
    collectionKey: 'services',
    items: [
      { title: 'Consulta inicial', summary: 'Primera valoración para conocer tu caso y definir el siguiente paso.', price: 600 },
      { title: 'Consulta de seguimiento', summary: 'Revisión de avance para pacientes ya en tratamiento.', price: 450 },
    ],
  },
  servicios: {
    collectionKey: 'services',
    items: [
      { title: 'Consultoría inicial', summary: 'Sesión de diagnóstico para entender tu necesidad y proponer un plan.', price: 1200 },
      { title: 'Acompañamiento mensual', summary: 'Seguimiento continuo con entregables definidos cada mes.', price: 6000 },
    ],
  },
  comercio: { collectionKey: 'products', items: GENERIC_PRODUCT_ITEMS },
  belleza: {
    collectionKey: 'services',
    items: [
      { title: 'Corte y peinado', summary: 'Servicio base de imagen para cualquier ocasión.', price: 350 },
      { title: 'Tratamiento facial', summary: 'Limpieza e hidratación profunda para el cuidado de la piel.', price: 700 },
    ],
  },
  educacion: {
    collectionKey: 'services',
    items: [
      { title: 'Clase individual', summary: 'Sesión personalizada según tu nivel y objetivo.', price: 400 },
      { title: 'Paquete mensual', summary: 'Cuatro sesiones semanales con seguimiento de avance.', price: 1400 },
    ],
  },
  fitness: {
    collectionKey: 'services',
    items: [
      { title: 'Sesión individual', summary: 'Entrenamiento personalizado según tu objetivo.', price: 350 },
      { title: 'Plan mensual', summary: 'Acceso ilimitado con seguimiento de tu progreso.', price: 1200 },
    ],
  },
  veterinaria: {
    collectionKey: 'services',
    items: [
      { title: 'Consulta general', summary: 'Revisión de salud y orientación para tu mascota.', price: 450 },
      { title: 'Vacunación', summary: 'Aplicación del esquema de vacunas con cartilla actualizada.', price: 350 },
    ],
  },
  automotriz: {
    collectionKey: 'services',
    items: [
      { title: 'Servicio de mantenimiento', summary: 'Revisión general y cambio de fluidos según kilometraje.', price: 900 },
      { title: 'Diagnóstico', summary: 'Revisión computarizada para detectar la falla antes de cotizar.', price: 300 },
    ],
  },
  construccion: {
    collectionKey: 'services',
    items: [
      { title: 'Visita de diagnóstico', summary: 'Evaluación en sitio para cotizar el proyecto con precisión.', price: 500 },
      { title: 'Remodelación por etapas', summary: 'Ejecución de obra dividida en fases con entregas parciales.' },
    ],
  },
  oficios: {
    collectionKey: 'services',
    items: [
      { title: 'Visita y diagnóstico', summary: 'Revisión en sitio para identificar el problema antes de cotizar.', price: 250 },
      { title: 'Servicio a domicilio', summary: 'Atención directa en tu casa o negocio.', price: 450 },
    ],
  },
  turismo: {
    collectionKey: 'services',
    items: [
      { title: 'Tour guiado', summary: 'Recorrido acompañado con guía certificado.', price: 800 },
      { title: 'Paquete personalizado', summary: 'Itinerario armado a tu medida según tus fechas y presupuesto.' },
    ],
  },
  legal: {
    collectionKey: 'services',
    items: [
      { title: 'Consulta legal inicial', summary: 'Revisión de tu caso y las opciones disponibles.', price: 800 },
      { title: 'Representación en trámite', summary: 'Acompañamiento legal completo durante todo el proceso.' },
    ],
  },
  eventos: {
    collectionKey: 'services',
    items: [
      { title: 'Paquete básico', summary: 'Cobertura esencial para tu evento.', price: 5000 },
      { title: 'Paquete integral', summary: 'Planeación completa de principio a fin.', price: 15000 },
    ],
  },
  software: {
    collectionKey: 'services',
    items: [
      { title: 'Sesión de descubrimiento', summary: 'Entendemos tu necesidad y proponemos un alcance de trabajo.' },
      { title: 'Desarrollo a la medida', summary: 'Construcción de tu proyecto con entregas iterativas.' },
    ],
  },
  // 'otro' declara su punto de partida explícito: quien no se reconoce en ningún giro
  // también merece una oferta que pueda editar, no la ausencia de una.
  otro: { collectionKey: 'services', items: GENERIC_SERVICE_ITEMS },
};

/** Reservado para una clave futura o heredada — nunca para una opción vigente del wizard. */
const DEFAULT_STARTER_OFFER: IndustryStarterOffer = {
  collectionKey: 'services',
  items: GENERIC_SERVICE_ITEMS,
};

/** Oferta inicial curada del giro (ONB-14): qué colección arranca y con qué contenido. */
export function industryStarterOffer(industry: string | null | undefined): IndustryStarterOffer {
  return (industry && INDUSTRY_STARTER_OFFER[industry]) || DEFAULT_STARTER_OFFER;
}

/** Giros con oferta inicial propia (ONB-14 §4 — inventario de cobertura). */
export const STARTER_OFFER_INDUSTRY_KEYS = Object.keys(INDUSTRY_STARTER_OFFER);

/**
 * Ítems de arranque para la colección que realmente se está sembrando. Cuando coincide
 * con la que el giro declara, es su contenido curado; cuando no (un restaurante que
 * además activó servicios), el punto de partida neutro de esa colección — nunca los
 * platillos de un menú disfrazados de servicio.
 */
export function starterOfferItemsFor(
  industry: string | null | undefined, collectionKey: StarterOfferCollectionKey,
): StarterOfferItem[] {
  const offer = industryStarterOffer(industry);
  return offer.collectionKey === collectionKey ? offer.items : GENERIC_STARTER_ITEMS[collectionKey];
}

/** Traduce un ítem de la oferta inicial a los campos que usa el preset destino. */
export function starterOfferItemFields(
  collectionKey: StarterOfferCollectionKey, item: StarterOfferItem,
): Record<string, unknown> {
  const body = collectionKey === 'services' ? { summary: item.summary } : { description: item.summary };
  return {
    title: item.title,
    ...body,
    ...(item.price != null ? { price: item.price } : {}),
    ...(collectionKey === 'menu' && item.category ? { category: item.category } : {}),
  };
}

// CLI-26 — los PROCESOS con los que arranca un tenant, uno por cada forma de trabajo que
// su giro realmente opera. No es el catálogo de `contact_statuses` (ese describe la
// relación con la persona: dónde va un lead); esto describe cómo AVANZA un trabajo: un
// avalúo, una consulta médica, un corte de cabello y una obra no recorren los mismos
// pasos aunque el lead sí.
//
// Sustituye al modelo de `CLI-25` (UNA secuencia por giro), que asumía que un giro opera
// un solo proceso: una inmobiliaria opera al menos cinco (venta, compra, renta, avalúo,
// asesoría hipotecaria) y describirlos con cuatro fases genéricas no le sirve a nadie.
//
// Reglas de redacción (decisión del founder 2026-08-24, que ensancha a propósito la
// frontera de `CLI-18`/`CLI-25`):
//  - Las etapas SÍ pueden nombrar hitos reales del giro — avalúo, firma en notaría,
//    escrituración, consentimiento informado — porque sin ellos el proceso no es
//    reconocible para quien lo opera.
//  - Siguen prohibidos los plazos ("en 15 días hábiles"), los artículos de ley y los
//    nombres de dependencias o autoridades. Cuando una etapa espera a una autoridad, el
//    tercero se nombra en genérico ("Autoridad local").
//  - Nada supone inventario, cobro ni disponibilidad en tiempo real.
// Todo es punto de partida editable: el dueño renombra, reordena o borra sin restricción.
//
// Cubre las 17 claves de `INDUSTRIES` (contrato en `industry-coverage.test.ts`);
// `DEFAULT_SERVICE_PROCESSES` queda para una clave futura o heredada, nunca para una
// opción vigente del wizard.
//
// Fuente del contenido: investigación por giro sobre prácticas del gremio en México
// (2026-08-24), curada contra las reglas de arriba. Detalle y nivel de confianza por
// giro en `docs/technical/panel/arranque-por-giro.md`.

/** Una etapa curada. Los campos opcionales solo se declaran cuando cambian el default:
 *  `in_progress` a cargo del negocio (y `done` en la última etapa del proceso). */
export interface ServiceStagePreset {
  name: string;
  /** `waiting_client` cuando la pelota queda del lado del cliente (autorizar, firmar,
   *  pagar, recoger); `waiting_business` cuando el proceso depende de un tercero. */
  category?: 'in_progress' | 'waiting_client' | 'waiting_business';
  responsibleParty?: 'business' | 'client' | 'external';
  /** CLI-23 — el nombre que el dueño usa para el tercero ("Notario", "Valuador"). No es
   *  una persona ni una entidad concreta, y nunca una dependencia o autoridad. */
  externalDependencyLabel?: string;
}

export interface ServiceProcessPreset {
  /** Cómo se llama el proceso en el panel. Es el nombre de la plantilla que se siembra. */
  name: string;
  stages: ServiceStagePreset[];
}

/** Espera al cliente: autorizar, firmar, pagar o recoger. */
const client: Pick<ServiceStagePreset, 'category' | 'responsibleParty'> = {
  category: 'waiting_client', responsibleParty: 'client',
};
/** Espera a un tercero con nombre propio (banco, notario, proveedor, laboratorio). */
const via = (label: string): Pick<ServiceStagePreset, 'category' | 'responsibleParty' | 'externalDependencyLabel'> => ({
  category: 'waiting_business', responsibleParty: 'external', externalDependencyLabel: label,
});

const INDUSTRY_SERVICE_PROCESSES: Record<string, ServiceProcessPreset[]> = {
  inmobiliaria: [
    {
      name: 'Venta (lado vendedor)',
      stages: [
        { name: 'Captación del inmueble' },
        { name: 'Acuerdo de comercialización', ...client },
        { name: 'Ficha y fotografía' },
        { name: 'Promoción y visitas' },
        { name: 'Oferta y negociación' },
        { name: 'Apartado', ...client },
        { name: 'Avalúo y crédito', ...via('Banco') },
        { name: 'Firma en notaría', ...via('Notario') },
        { name: 'Entrega del inmueble' },
      ],
    },
    {
      name: 'Compra (acompañamiento)',
      stages: [
        { name: 'Levantamiento de necesidades' },
        { name: 'Preselección de opciones' },
        { name: 'Visitas' },
        { name: 'Oferta y negociación' },
        { name: 'Apartado', ...client },
        { name: 'Gestión de crédito', ...via('Banco') },
        { name: 'Firma en notaría', ...via('Notario') },
        { name: 'Entrega del inmueble' },
      ],
    },
    {
      name: 'Renta',
      stages: [
        { name: 'Captación del inmueble' },
        { name: 'Ficha y difusión' },
        { name: 'Visitas' },
        { name: 'Selección de inquilino' },
        { name: 'Contrato de arrendamiento', ...client },
        { name: 'Entrega de llaves' },
      ],
    },
    {
      name: 'Avalúo',
      stages: [
        { name: 'Solicitud de avalúo' },
        { name: 'Visita del valuador', ...via('Valuador') },
        { name: 'Levantamiento físico' },
        { name: 'Análisis comparativo' },
        { name: 'Elaboración del informe' },
        { name: 'Entrega del informe' },
      ],
    },
    {
      name: 'Asesoría hipotecaria',
      stages: [
        { name: 'Levantamiento de perfil' },
        { name: 'Precalificación', ...via('Banco') },
        { name: 'Integración del expediente' },
        { name: 'Firma del contrato de crédito', ...via('Banco') },
        { name: 'Cierre en notaría' },
      ],
    },
  ],
  restaurante: [
    {
      name: 'Reservación de grupo',
      stages: [
        { name: 'Solicitud de reservación' },
        { name: 'Disponibilidad confirmada' },
        { name: 'Reservación registrada' },
        { name: 'Recordatorio previo' },
        { name: 'Servicio del día' },
        { name: 'Cierre de mesa' },
      ],
    },
    {
      name: 'Evento privado',
      stages: [
        { name: 'Contacto inicial' },
        { name: 'Cotización del menú' },
        { name: 'Confirmación con anticipo', ...client },
        { name: 'Coordinación con cocina' },
        { name: 'Montaje y preparación' },
        { name: 'Realización del evento' },
        { name: 'Cierre del evento' },
      ],
    },
    {
      name: 'Pedido por encargo',
      stages: [
        { name: 'Solicitud del pedido' },
        { name: 'Diseño y sabores definidos' },
        { name: 'Cotización aprobada', ...client },
        { name: 'Producción' },
        { name: 'Empaque' },
        { name: 'Entrega o recolección' },
      ],
    },
  ],
  salud: [
    {
      name: 'Consulta de primera vez',
      stages: [
        { name: 'Solicitud de cita' },
        { name: 'Registro del paciente' },
        { name: 'Historia clínica' },
        { name: 'Exploración y diagnóstico' },
        { name: 'Estudios indicados', ...via('Laboratorio') },
        { name: 'Plan de tratamiento' },
        { name: 'Próxima cita agendada' },
      ],
    },
    {
      name: 'Consulta de seguimiento',
      stages: [
        { name: 'Cita de seguimiento' },
        { name: 'Revisión de evolución' },
        { name: 'Ajuste del tratamiento' },
        { name: 'Próxima cita agendada' },
      ],
    },
    {
      name: 'Tratamiento con procedimiento',
      stages: [
        { name: 'Valoración inicial' },
        { name: 'Diagnóstico y plan de tratamiento' },
        { name: 'Presupuesto' },
        { name: 'Consentimiento informado', ...client },
        { name: 'Procedimiento realizado' },
        { name: 'Revisión posterior' },
      ],
    },
  ],
  servicios: [
    {
      name: 'Proyecto de consultoría',
      stages: [
        { name: 'Contacto y diagnóstico inicial' },
        { name: 'Propuesta de alcance' },
        { name: 'Firma del contrato', ...client },
        { name: 'Levantamiento de información' },
        { name: 'Desarrollo del trabajo' },
        { name: 'Presentación de entregables' },
        { name: 'Cierre y ajustes' },
      ],
    },
    {
      name: 'Contabilidad mensual',
      stages: [
        { name: 'Alta del cliente' },
        { name: 'Recepción de documentos', ...client },
        { name: 'Captura y conciliación' },
        { name: 'Elaboración de declaraciones' },
        { name: 'Revisión con el cliente', ...client },
        { name: 'Entrega de reportes' },
      ],
    },
    {
      name: 'Servicio por encargo',
      stages: [
        { name: 'Solicitud del cliente' },
        { name: 'Cotización' },
        { name: 'Confirmación del cliente', ...client },
        { name: 'Programación de la visita' },
        { name: 'Ejecución del servicio' },
        { name: 'Conformidad del cliente', ...client },
        { name: 'Cierre del servicio' },
      ],
    },
    {
      name: 'Servicio recurrente',
      stages: [
        { name: 'Alta del cliente' },
        { name: 'Acuerdo de alcance y periodicidad' },
        { name: 'Confirmación del acuerdo', ...client },
        { name: 'Preparación del periodo' },
        { name: 'Servicio del periodo' },
        { name: 'Reporte al cliente' },
        { name: 'Renovación o cierre' },
      ],
    },
    {
      name: 'Garantía o reclamo',
      stages: [
        { name: 'Reporte del cliente' },
        { name: 'Revisión de lo ocurrido' },
        { name: 'Diagnóstico' },
        { name: 'Propuesta de solución', ...client },
        { name: 'Corrección' },
        { name: 'Conformidad del cliente', ...client },
        { name: 'Cierre' },
      ],
    },
  ],
  comercio: [
    {
      name: 'Pedido especial',
      stages: [
        { name: 'Solicitud del cliente' },
        { name: 'Cotización con proveedor', ...via('Proveedor') },
        { name: 'Confirmación de precio', ...client },
        { name: 'Pedido al proveedor', ...via('Proveedor') },
        { name: 'Recepción de la mercancía' },
        { name: 'Aviso al cliente', ...client },
        { name: 'Entrega' },
      ],
    },
    {
      name: 'Apartado',
      stages: [
        { name: 'Selección del producto' },
        { name: 'Apartado registrado' },
        { name: 'Anticipo recibido', ...client },
        { name: 'Resguardo en tienda', ...client },
        { name: 'Liquidación y entrega' },
      ],
    },
    {
      name: 'Entrega a domicilio',
      stages: [
        { name: 'Pedido del cliente' },
        { name: 'Preparación del pedido' },
        { name: 'Asignación de la entrega' },
        { name: 'Traslado' },
        { name: 'Entrega confirmada' },
      ],
    },
  ],
  belleza: [
    {
      name: 'Cita de salón',
      stages: [
        { name: 'Reservación de cita' },
        { name: 'Cita confirmada' },
        { name: 'Recepción y valoración' },
        { name: 'Servicio realizado' },
        { name: 'Recomendaciones finales' },
        { name: 'Cobro y salida' },
      ],
    },
    {
      name: 'Tratamiento por sesiones',
      stages: [
        { name: 'Valoración inicial' },
        { name: 'Plan de sesiones acordado', ...client },
        { name: 'Programación de citas' },
        { name: 'Sesiones en curso' },
        { name: 'Evaluación de resultados' },
      ],
    },
    {
      name: 'Servicio de novia o evento',
      stages: [
        { name: 'Consulta de imagen' },
        { name: 'Cotización del paquete' },
        { name: 'Confirmación con anticipo', ...client },
        { name: 'Prueba previa' },
        { name: 'Día del evento' },
        { name: 'Cierre del servicio' },
      ],
    },
    {
      name: 'Cambio de imagen o color',
      stages: [
        { name: 'Diagnóstico del cabello' },
        { name: 'Propuesta y cotización' },
        { name: 'Autorización del cliente', ...client },
        { name: 'Aplicación del servicio' },
        { name: 'Cuidados en casa' },
        { name: 'Cita de retoque' },
      ],
    },
    {
      name: 'Corrección o retoque',
      stages: [
        { name: 'Reporte del cliente' },
        { name: 'Valoración de lo ocurrido' },
        { name: 'Propuesta de corrección', ...client },
        { name: 'Servicio de corrección' },
        { name: 'Conformidad del cliente', ...client },
        { name: 'Cierre' },
      ],
    },
  ],
  educacion: [
    {
      name: 'Inscripción a curso',
      stages: [
        { name: 'Solicitud de información' },
        { name: 'Evaluación de nivel' },
        { name: 'Propuesta de plan de estudio' },
        { name: 'Inscripción y pago', ...client },
        { name: 'Asignación de grupo' },
        { name: 'Inicio de clases' },
      ],
    },
    {
      name: 'Clases particulares',
      stages: [
        { name: 'Solicitud del alumno' },
        { name: 'Diagnóstico de nivel' },
        { name: 'Programación de sesiones' },
        { name: 'Sesiones en curso' },
        { name: 'Evaluación de avance' },
        { name: 'Cierre o renovación' },
      ],
    },
    {
      name: 'Taller o curso corto',
      stages: [
        { name: 'Convocatoria abierta' },
        { name: 'Registro del participante' },
        { name: 'Confirmación de lugar', ...client },
        { name: 'Preparación de materiales' },
        { name: 'Impartición del taller' },
        { name: 'Entrega de constancias' },
      ],
    },
    {
      name: 'Capacitación para empresa',
      stages: [
        { name: 'Solicitud de la empresa' },
        { name: 'Diagnóstico de necesidades' },
        { name: 'Propuesta y cotización' },
        { name: 'Aprobación de la empresa', ...client },
        { name: 'Programación del grupo' },
        { name: 'Impartición' },
        { name: 'Reporte de resultados' },
      ],
    },
    {
      name: 'Renovación de ciclo',
      stages: [
        { name: 'Aviso de fin de ciclo' },
        { name: 'Revisión de avance' },
        { name: 'Propuesta del siguiente nivel' },
        { name: 'Reinscripción', ...client },
        { name: 'Asignación de grupo' },
        { name: 'Inicio del nuevo ciclo' },
      ],
    },
  ],
  fitness: [
    {
      name: 'Alta de membresía',
      stages: [
        { name: 'Visita inicial' },
        { name: 'Evaluación física' },
        { name: 'Planes presentados' },
        { name: 'Firma de la membresía', ...client },
        { name: 'Inducción a las instalaciones' },
        { name: 'Primera sesión' },
      ],
    },
    {
      name: 'Entrenamiento personalizado',
      stages: [
        { name: 'Evaluación inicial' },
        { name: 'Objetivos definidos', ...client },
        { name: 'Diseño del plan' },
        { name: 'Sesiones periódicas' },
        { name: 'Reevaluación de progreso' },
      ],
    },
    {
      name: 'Plan de nutrición',
      stages: [
        { name: 'Consulta inicial' },
        { name: 'Medición y diagnóstico' },
        { name: 'Plan entregado' },
        { name: 'Seguimiento del periodo' },
        { name: 'Nueva medición' },
        { name: 'Ajuste del plan' },
        { name: 'Cierre del ciclo' },
      ],
    },
    {
      name: 'Clase grupal o reto',
      stages: [
        { name: 'Inscripción al grupo' },
        { name: 'Confirmación de lugar', ...client },
        { name: 'Evaluación de arranque' },
        { name: 'Sesiones en curso' },
        { name: 'Evaluación final' },
        { name: 'Entrega de resultados' },
      ],
    },
    {
      name: 'Renovación de membresía',
      stages: [
        { name: 'Aviso de vencimiento' },
        { name: 'Contacto con el miembro' },
        { name: 'Propuesta de renovación' },
        { name: 'Confirmación del miembro', ...client },
        { name: 'Membresía reactivada' },
        { name: 'Seguimiento de regreso' },
      ],
    },
  ],
  veterinaria: [
    {
      name: 'Consulta veterinaria',
      stages: [
        { name: 'Solicitud de cita' },
        { name: 'Registro del paciente' },
        { name: 'Exploración y diagnóstico' },
        { name: 'Estudios indicados', ...via('Laboratorio') },
        { name: 'Tratamiento o receta' },
        { name: 'Próxima visita agendada' },
      ],
    },
    {
      name: 'Hospitalización',
      stages: [
        { name: 'Ingreso del paciente' },
        { name: 'Diagnóstico y plan terapéutico' },
        { name: 'Autorización del propietario', ...client },
        { name: 'Monitoreo y tratamiento' },
        { name: 'Alta médica' },
        { name: 'Entrega al propietario' },
      ],
    },
    {
      name: 'Estética y baño',
      stages: [
        { name: 'Recepción de la mascota' },
        { name: 'Valoración del servicio' },
        { name: 'Servicio realizado' },
        { name: 'Aviso de término', ...client },
        { name: 'Entrega al propietario' },
      ],
    },
  ],
  automotriz: [
    {
      name: 'Reparación mecánica',
      stages: [
        { name: 'Recepción del vehículo' },
        { name: 'Diagnóstico' },
        { name: 'Presupuesto elaborado' },
        { name: 'Autorización del cliente', ...client },
        { name: 'Reparación' },
        { name: 'Control de calidad' },
        { name: 'Entrega del vehículo' },
      ],
    },
    {
      name: 'Hojalatería y pintura',
      stages: [
        { name: 'Recepción y valoración de daños' },
        { name: 'Presupuesto con la aseguradora', ...via('Aseguradora') },
        { name: 'Autorización del cliente', ...client },
        { name: 'Desarme y reparación' },
        { name: 'Pintura y acabado' },
        { name: 'Entrega del vehículo' },
      ],
    },
    {
      name: 'Detallado',
      stages: [
        { name: 'Recepción del vehículo' },
        { name: 'Diagnóstico del estado' },
        { name: 'Cotización aprobada', ...client },
        { name: 'Servicio realizado' },
        { name: 'Entrega del vehículo' },
      ],
    },
  ],
  construccion: [
    {
      name: 'Obra nueva o remodelación',
      stages: [
        { name: 'Levantamiento y diagnóstico' },
        { name: 'Anteproyecto' },
        { name: 'Presupuesto y contrato', ...client },
        { name: 'Gestión de permisos', ...via('Autoridad local') },
        { name: 'Ejecución de la obra' },
        { name: 'Entrega de la obra' },
      ],
    },
    {
      name: 'Diseño arquitectónico',
      stages: [
        { name: 'Entrevista y necesidades' },
        { name: 'Propuesta conceptual' },
        { name: 'Anteproyecto aprobado', ...client },
        { name: 'Planos ejecutivos' },
        { name: 'Supervisión de obra' },
        { name: 'Entrega final' },
      ],
    },
    {
      name: 'Presupuesto de obra',
      stages: [
        { name: 'Solicitud del cliente' },
        { name: 'Visita al sitio' },
        { name: 'Levantamiento de medidas' },
        { name: 'Elaboración del presupuesto' },
        { name: 'Presentación al cliente' },
        { name: 'Decisión del cliente', ...client },
        { name: 'Cierre del presupuesto' },
      ],
    },
    {
      name: 'Mantenimiento o reparación',
      stages: [
        { name: 'Reporte del cliente' },
        { name: 'Diagnóstico en sitio' },
        { name: 'Cotización' },
        { name: 'Autorización del cliente', ...client },
        { name: 'Ejecución del trabajo' },
        { name: 'Entrega del trabajo' },
      ],
    },
    {
      name: 'Gestión de permisos',
      stages: [
        { name: 'Documentos del propietario', ...client },
        { name: 'Integración del expediente' },
        { name: 'Presentación del trámite', ...via('Autoridad local') },
        { name: 'Observaciones y correcciones' },
        { name: 'Resolución', ...via('Autoridad local') },
        { name: 'Entrega al cliente' },
      ],
    },
  ],
  oficios: [
    {
      name: 'Servicio a domicilio',
      stages: [
        { name: 'Solicitud del servicio' },
        { name: 'Diagnóstico en sitio' },
        { name: 'Cotización' },
        { name: 'Autorización del cliente', ...client },
        { name: 'Trabajo realizado' },
        { name: 'Revisión final y entrega' },
      ],
    },
    {
      name: 'Jardinería y mantenimiento',
      stages: [
        { name: 'Visita de diagnóstico' },
        { name: 'Propuesta de trabajo' },
        { name: 'Autorización del cliente', ...client },
        { name: 'Trabajo realizado' },
        { name: 'Entrega y recomendaciones' },
      ],
    },
    {
      name: 'Instalación',
      stages: [
        { name: 'Solicitud del cliente' },
        { name: 'Visita de medición' },
        { name: 'Cotización' },
        { name: 'Autorización del cliente', ...client },
        { name: 'Preparación del material' },
        { name: 'Instalación en sitio' },
        { name: 'Prueba de funcionamiento' },
        { name: 'Entrega del trabajo' },
      ],
    },
    {
      name: 'Servicio urgente',
      stages: [
        { name: 'Aviso del cliente' },
        { name: 'Confirmación de la visita' },
        { name: 'Atención en sitio' },
        { name: 'Solución aplicada' },
        { name: 'Cotización de lo pendiente' },
        { name: 'Cierre del servicio' },
      ],
    },
    {
      name: 'Mantenimiento por contrato',
      stages: [
        { name: 'Alta del cliente' },
        { name: 'Acuerdo de visitas' },
        { name: 'Confirmación del acuerdo', ...client },
        { name: 'Visita programada' },
        { name: 'Reporte de la visita' },
        { name: 'Renovación o cierre' },
      ],
    },
  ],
  turismo: [
    {
      name: 'Reservación de hospedaje',
      stages: [
        { name: 'Consulta de disponibilidad' },
        { name: 'Cotización' },
        { name: 'Confirmación con anticipo', ...client },
        { name: 'Preparación de la habitación' },
        { name: 'Check-in' },
        { name: 'Estancia' },
        { name: 'Check-out' },
      ],
    },
    {
      name: 'Tour o experiencia',
      stages: [
        { name: 'Solicitud de reservación' },
        { name: 'Cupo confirmado' },
        { name: 'Coordinación con el proveedor', ...via('Proveedor') },
        { name: 'Punto de encuentro' },
        { name: 'Tour realizado' },
        { name: 'Cierre' },
      ],
    },
    {
      name: 'Paquete de viaje',
      stages: [
        { name: 'Levantamiento de preferencias' },
        { name: 'Cotización del itinerario' },
        { name: 'Reservación con proveedores', ...via('Proveedor') },
        { name: 'Confirmación y pago', ...client },
        { name: 'Entrega de documentos de viaje' },
      ],
    },
  ],
  legal: [
    {
      name: 'Asesoría o caso legal',
      stages: [
        { name: 'Consulta inicial' },
        { name: 'Análisis de viabilidad' },
        { name: 'Propuesta de honorarios' },
        { name: 'Firma del contrato', ...client },
        { name: 'Integración del expediente' },
        { name: 'Gestión del asunto' },
        { name: 'Resolución y cierre' },
      ],
    },
    {
      name: 'Trámite notarial',
      stages: [
        { name: 'Recepción de la solicitud' },
        { name: 'Revisión de documentación' },
        { name: 'Correcciones del expediente', ...client },
        { name: 'Elaboración del instrumento' },
        { name: 'Firma en notaría', ...via('Notario') },
        { name: 'Inscripción y entrega' },
      ],
    },
    {
      name: 'Juicio o litigio',
      stages: [
        { name: 'Consulta inicial' },
        { name: 'Análisis de viabilidad' },
        { name: 'Integración de pruebas', ...client },
        { name: 'Presentación de la demanda' },
        { name: 'Contestación y desahogo' },
        { name: 'Audiencias' },
        { name: 'Resolución', ...via('Autoridad') },
        { name: 'Cumplimiento y cierre' },
      ],
    },
    {
      name: 'Constitución de empresa',
      stages: [
        { name: 'Definición de la figura legal' },
        { name: 'Datos y documentos de los socios', ...client },
        { name: 'Elaboración del acta constitutiva' },
        { name: 'Firma en notaría', ...via('Notario') },
        { name: 'Inscripción en el registro', ...via('Autoridad') },
        { name: 'Alta fiscal', ...via('Autoridad') },
        { name: 'Entrega de documentos' },
      ],
    },
    {
      name: 'Contratos y convenios',
      stages: [
        { name: 'Definición de la necesidad' },
        { name: 'Recopilación de información', ...client },
        { name: 'Redacción del contrato' },
        { name: 'Revisión con las partes', ...client },
        { name: 'Ajustes finales' },
        { name: 'Firma', ...client },
        { name: 'Entrega de la versión firmada' },
      ],
    },
  ],
  eventos: [
    {
      name: 'Organización de evento',
      stages: [
        { name: 'Entrevista y conceptualización' },
        { name: 'Propuesta y cotización' },
        { name: 'Contratación y anticipo', ...client },
        { name: 'Coordinación de proveedores', ...via('Proveedor') },
        { name: 'Revisión final' },
        { name: 'Día del evento' },
        { name: 'Cierre del evento' },
      ],
    },
    {
      name: 'Servicio para un evento',
      stages: [
        { name: 'Solicitud del servicio' },
        { name: 'Cotización' },
        { name: 'Confirmación con anticipo', ...client },
        { name: 'Preparación previa' },
        { name: 'Servicio realizado' },
        { name: 'Entrega de material o cierre' },
      ],
    },
    {
      name: 'Cotización de evento',
      stages: [
        { name: 'Solicitud del cliente' },
        { name: 'Entrevista de necesidades' },
        { name: 'Propuesta y cotización' },
        { name: 'Ajustes a la propuesta' },
        { name: 'Decisión del cliente', ...client },
        { name: 'Cierre de la cotización' },
      ],
    },
    {
      name: 'Renta de mobiliario o equipo',
      stages: [
        { name: 'Solicitud del cliente' },
        { name: 'Confirmación de fecha' },
        { name: 'Cotización' },
        { name: 'Confirmación con anticipo', ...client },
        { name: 'Entrega e instalación' },
        { name: 'Recolección' },
        { name: 'Cierre de la renta' },
      ],
    },
  ],
  software: [
    {
      name: 'Desarrollo a la medida',
      stages: [
        { name: 'Levantamiento de requerimientos' },
        { name: 'Propuesta y alcance' },
        { name: 'Firma del contrato', ...client },
        { name: 'Diseño y desarrollo' },
        { name: 'Pruebas' },
        { name: 'Entrega y puesta en marcha' },
      ],
    },
    {
      name: 'Alta de cliente nuevo',
      stages: [
        { name: 'Solicitud de prueba o demostración' },
        { name: 'Configuración de la cuenta' },
        { name: 'Capacitación inicial' },
        { name: 'Uso activo' },
        { name: 'Revisión de adopción' },
      ],
    },
    {
      name: 'Soporte o incidencia',
      stages: [
        { name: 'Reporte del cliente' },
        { name: 'Revisión de lo ocurrido' },
        { name: 'Diagnóstico' },
        { name: 'Solución aplicada' },
        { name: 'Validación con el cliente', ...client },
        { name: 'Cierre del reporte' },
      ],
    },
    {
      name: 'Implementación o migración',
      stages: [
        { name: 'Plan de implementación' },
        { name: 'Preparación de la información', ...client },
        { name: 'Configuración' },
        { name: 'Pruebas con el cliente', ...client },
        { name: 'Puesta en marcha' },
        { name: 'Acompañamiento posterior' },
      ],
    },
    {
      name: 'Renovación de suscripción',
      stages: [
        { name: 'Aviso de renovación' },
        { name: 'Revisión de uso' },
        { name: 'Propuesta de renovación' },
        { name: 'Confirmación del cliente', ...client },
        { name: 'Renovación aplicada' },
        { name: 'Seguimiento del nuevo periodo' },
      ],
    },
  ],
  otro: [
    {
      name: 'Atención de un trabajo',
      stages: [
        { name: 'Solicitud del cliente' },
        { name: 'Diagnóstico de la necesidad' },
        { name: 'Cotización o propuesta' },
        { name: 'Autorización del cliente', ...client },
        { name: 'Trabajo realizado' },
        { name: 'Entrega y cierre' },
      ],
    },
  ],
};

/**
 * Giros que operan UN solo proceso a propósito. Declararlo aquí es obligatorio: la
 * prueba de cobertura falla si un giro trae una sola secuencia sin estar en esta lista,
 * que es exactamente el descuido que `CLI-26` vino a corregir.
 *
 * `otro` es la única clave del wizard que no describe una operación concreta: quien la
 * elige no comparte proceso con nadie más, así que recibe la secuencia genérica que
 * cualquier negocio reconoce y la ajusta desde el panel.
 */
export const SINGLE_PROCESS_INDUSTRIES: readonly string[] = ['otro'];

/** Reservado para una clave futura o heredada — nunca para una opción vigente del wizard. */
const DEFAULT_SERVICE_PROCESSES: ServiceProcessPreset[] = INDUSTRY_SERVICE_PROCESSES.otro;

export interface ServiceStageSeed {
  name: string;
  category: 'in_progress' | 'waiting_client' | 'waiting_business' | 'done';
  responsibleParty: 'business' | 'client' | 'external';
  externalDependencyLabel: string | null;
  sortOrder: number;
}

export interface ServiceProcessSeed {
  name: string;
  stages: ServiceStageSeed[];
}

/**
 * Procesos curados del giro, listos para sembrar (CLI-26). La última etapa de cada uno es
 * `done` a cargo del negocio — sin una etapa terminal un trabajo no tendría forma de
 * completarse — y el resto usa lo que declare el catálogo, con `in_progress` a cargo del
 * negocio como default.
 */
export function industryServiceProcesses(industry: string | null | undefined): ServiceProcessSeed[] {
  const processes = (industry && INDUSTRY_SERVICE_PROCESSES[industry]) || DEFAULT_SERVICE_PROCESSES;
  return processes.map((process) => ({
    name: process.name,
    stages: process.stages.map((stage, sortOrder) => {
      const last = sortOrder === process.stages.length - 1;
      return {
        name: stage.name,
        category: last ? 'done' : (stage.category ?? 'in_progress'),
        responsibleParty: last ? 'business' : (stage.responsibleParty ?? 'business'),
        externalDependencyLabel: last ? null : (stage.externalDependencyLabel ?? null),
        sortOrder,
      };
    }),
  }));
}

/** Giros con procesos propios, y solo si ninguno quedó vacío (CLI-26 / ONB-14 §4 —
 *  inventario de cobertura: un giro con la lista vacía cuenta como no cubierto). */
export const SERVICE_PROCESS_INDUSTRY_KEYS = Object.entries(INDUSTRY_SERVICE_PROCESSES)
  .filter(([, processes]) => processes.length > 0 && processes.every((process) => process.stages.length > 0))
  .map(([key]) => key);

// HIP-03 — sugerencias de plazo por giro, punto de partida editable (nunca impuesto).
// `veterinaria`/`automotriz` tienen patrón propio (ciclo que se repite, no expediente que
// se completa — ver la ficha); el resto usa las credenciales genéricas de cualquier
// negocio. `recurrenceDays: null` = sugiere una fecha única, no recurrente.
export interface ExpirySuggestion {
  label: string;
  recurrenceDays: number | null;
}

const INDUSTRY_EXPIRY_SUGGESTIONS: Record<string, ExpirySuggestion[]> = {
  veterinaria: [
    { label: 'Vacuna (refuerzo)', recurrenceDays: 365 },
    { label: 'Verificación o control semestral', recurrenceDays: 180 },
  ],
  automotriz: [
    { label: 'Verificación vehicular', recurrenceDays: 180 },
  ],
};

const DEFAULT_EXPIRY_SUGGESTIONS: ExpirySuggestion[] = [
  { label: 'Licencia o permiso del negocio', recurrenceDays: 365 },
  { label: 'Póliza de seguro', recurrenceDays: 365 },
];

export function industryExpirySuggestions(industry: string | null | undefined): ExpirySuggestion[] {
  return [...(industry ? INDUSTRY_EXPIRY_SUGGESTIONS[industry] ?? [] : []), ...DEFAULT_EXPIRY_SUGGESTIONS];
}

/** Giros con vigencias propias (ONB-14 §4 — inventario de cobertura). */
export const EXPIRY_INDUSTRY_KEYS = Object.keys(INDUSTRY_EXPIRY_SUGGESTIONS);

// Convierte un preset en los campos de una fila de `collection_types` (serializa
// los JSON). El worker añade id/tenantId/createdAt. sortOrder = orden de elección.
export function presetToRow(preset: CollectionTypePreset, sortOrder: number) {
  return {
    key: preset.key,
    archetype: preset.archetype,
    labelsJson: JSON.stringify(preset.labels),
    routeJson: JSON.stringify(preset.route),
    icon: preset.icon,
    jsonLdType: preset.jsonLdType,
    hasDetailPage: preset.hasDetailPage,
    schemaJson: JSON.stringify(preset.schema),
    sortOrder,
  };
}
